/**
 * HANDY FUTURE ARENA — 게임 탭 전체
 *
 * 기획 목업(12장) 그대로 옮긴 화면이다. 5개 뷰가 한 컴포넌트 안에 있다.
 *   lobby  핸디 아레나 로비        (ui03 / ui08)
 *   boss   BTC 보스 레이드         (ui04 / ui05)
 *   royale HANDY ROYALE           (ui09 / ui12)
 *   rank   핸디 리그 랭킹          (ui02 / ui11)
 *   mission 미션·보상              (ui06)
 *
 * 화면이 살아있게 하려고 3초마다 로비를 다시 읽는다. 보스 HP·참여자·남은 시간이
 * 계속 움직여야 "지금 같이 하고 있다" 는 느낌이 난다.
 * 라운드 남은 시간은 서버 시각(locksAt)을 기준으로 클라이언트가 계산한다 —
 * 매초 서버를 부르면 1,000명이 붙었을 때 초당 1,000 요청이 된다.
 *
 * 스타일은 .ga 스코프 안에서만 쓴다. 지갑 나머지 화면은 라이트 테마라
 * 전역에 다크 토큰을 풀면 안 된다.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { RoyalPage } from './RoyalPage';
import { toast } from 'sonner';
import { BtcLiveChart, type Mark } from './BtcLiveChart';
import { Users, RotateCw, Trophy, Wallet, TrendingUp, TrendingDown, Crown, Gift } from 'lucide-react';
import { FreeWheel } from './FreeWheel';

interface Props { authHeaders: () => Record<string, string>; }

const A = '/game';   // 에셋 경로 (vite base=/dev/)

const fmt = (n: number, d = 0) =>
  Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: d });

/** 남은 초 → MM:SS */
function mmss(sec: number) {
  const s = Math.max(0, Math.floor(sec));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}
/** 남은 초 → [시,분,초] 2자리 문자열 (카운트다운 박스용) */
function hms(sec: number): [string, string, string] {
  const s = Math.max(0, Math.floor(sec));
  return [
    String(Math.floor(s / 3600)).padStart(2, '0'),
    String(Math.floor((s % 3600) / 60)).padStart(2, '0'),
    String(s % 60).padStart(2, '0'),
  ];
}
/** 남은 초 → HH:MM:SS (한 줄 표기용). mmss 는 60분이 넘으면 "1410:37" 처럼 깨진다. */
function hmsStr(sec: number) {
  return hms(sec).join(':');
}
const secsTo = (iso?: string | null) =>
  iso ? Math.max(0, (new Date(iso).getTime() - Date.now()) / 1000) : 0;

/** 백엔드는 'UP'/'DOWN'(대문자)만 받고, 조회 결과도 대문자로 돌려준다. */
const UP = (v?: string | null) => String(v || '').toUpperCase() === 'UP';
const DOWN = (v?: string | null) => String(v || '').toUpperCase() === 'DOWN';

const LEAGUE: Record<string, { label: string; color: string }> = {
  bronze:   { label: 'BRONZE',   color: '#C98A4B' },
  silver:   { label: 'SILVER',   color: '#B8C4D9' },
  gold:     { label: 'GOLD',     color: '#FFC53D' },
  platinum: { label: 'PLATINUM', color: '#4FD1C5' },
  diamond:  { label: 'DIAMOND',  color: '#60A5FA' },
};
const leagueOf = (v?: string) => LEAGUE[String(v || 'bronze').toLowerCase()] || LEAGUE.bronze;

/**
 * 클랜 엠블럼. 실제로 있는 파일만 허용한다.
 * (item-shield-gold 는 원본 스프라이트시트에서부터 잘려 있어서 폐기했다.
 *  DB 에 그 이름이 남아 있어도 여기서 걸러진다.)
 */
const EMBLEMS = new Set(['item-shield', 'item-trophy', 'item-crown', 'item-fire', 'item-coin']);
const emblem = (v?: string | null) =>
  EMBLEMS.has(String(v || '')) ? String(v) : 'item-shield';

/** 미션 그림 — 전부 보스 배너를 쓰고 있어서 네 줄이 똑같아 보였다 */
function missionArt(code?: string) {
  const c = String(code || '');
  if (c.includes('boss')) return 'boss-golem';
  if (c.includes('pick10')) return 'item-calendar10';
  if (c.includes('royale')) return 'item-crown';
  if (c.includes('invite')) return 'char-highfive';
  if (c.includes('streak')) return 'item-fire';
  if (c.includes('wallet')) return 'item-coin';
  return 'item-coins';
}

/** 남은 초 → "3일 12시간" 같은 사람 말 */
function dLeft(sec: number) {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  if (d > 0) return `${d}일 ${h}시간`;
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}시간 ${m}분` : `${m}분`;
}

export function GameArena({ authHeaders }: Props) {
  const [lobby, setLobby] = useState<any>(null);
  const [view, setView] = useState<
    'royale' | 'boss' | 'royale' | 'humanai' | 'clan' | 'rank' | 'mission' | 'shop'>('royale');
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [rank, setRank] = useState<any>(null);
  const [rankScope, setRankScope] = useState<'all' | 'friend' | 'clan'>('all');
  const [missions, setMissions] = useState<any[]>([]);
  const [royale, setRoyale] = useState<any>(null);

  // 오늘 쏘는 금액 — "핸디로얄이 매일 $10,000 을 쏩니다" 배너와 마감 팝업이 쓴다.
  // royale 응답에도 같이 오지만, 판이 없을 때(session:null)는 안 오므로 따로 읽는다.
  const [cap, setCap] = useState<any>(null);
  const [capClosedSeen, setCapClosedSeen] = useState(false);

  // 연속 참여 보너스 상태 — 로더는 H() 선언 뒤에 둔다 (2026-10-17)
  const [streakInfo, setStreakInfo] = useState<any>(null);
  const [myLog, setMyLog] = useState<any>(null);
  const [boxPrize, setBoxPrize] = useState<number | null>(null);

  // 소각 내역 — 공지한 걸 화면으로 증명하는 자리다 (2026-10-16)
  const [burns, setBurns] = useState<any>(null);
  useEffect(() => {
    let dead = false;
    const pull = () => fetch('/api/game/burns?limit=10')
      .then(r => r.json())
      .then(j => { if (!dead && j?.success) setBurns(j); })
      .catch(() => {});
    pull();
    const t = setInterval(pull, 60000);
    return () => { dead = true; clearInterval(t); };
  }, []);
  useEffect(() => {
    let dead = false;
    const pull = async () => {
      try {
        const j = await fetch('/api/game/daily-cap').then(r => r.json());
        if (!dead && j?.success) setCap(j);
      } catch { /* 배너 때문에 화면이 죽으면 안 된다 */ }
    };
    pull();
    const t = setInterval(pull, 15000);
    return () => { dead = true; clearInterval(t); };
  }, []);
  // 내가 언제·얼마에·어느 쪽으로 걸었는지. 그래프에 점으로 찍는다.
  const [mark, setMark] = useState<Mark>(null);
  // 라운드가 끝나는 순간을 잡아 크게 알려주기 위한 것. 직전 상태를 기억해둔다.
  const prevRoyale = useRef<{ round: number; alive: number; myAlive: boolean } | null>(null);
  const [royFlash, setRoyFlash] = useState<{ t: string; d: string; tone: 'win' | 'lose' | 'info' } | null>(null);
  // 게임에서 나갈 때 보여줄 결과
  const [result, setResult] = useState<
    { bet: number; count: number; wins: number; profit: number; round: number } | null>(null);
  const [wheel, setWheel] = useState(false);
  // 베팅 금액과 배수. 배수는 맞혔을 때만 곱해진다(틀리면 건 돈만 잃는다).
  const [betAmt, setBetAmt] = useState<number>(0);
  const [lev, setLev] = useState<number>(1);
  const [showLedger, setShowLedger] = useState(false);
  const buzzed = useRef<number>(-1);
  // 로얄 안내 이미지 펼침 여부 — 기본은 접어둔다
  const [royalInfo, setRoyalInfo] = useState(false);
  const [hva, setHva] = useState<any>(null);
  const [clanWar, setClanWar] = useState<any>(null);
  const [clanList, setClanList] = useState<any>(null);
  const [season, setSeason] = useState<any>(null);
  const [newClan, setNewClan] = useState({ name: '', tag: '' });
  const seenRound = useRef<number | null>(null);
  const prevHp = useRef<number | null>(null);
  const [flash, setFlash] = useState<any>(null);   // 라운드 결과 팝업
  const [stake, setStake] = useState<1 | 3 | 10>(1);
  const [coins, setCoins] = useState<any>(null);
  const [srun, setSrun] = useState<any>(null);
  const [shop, setShop] = useState<any>(null);

  /**
   * 부모(App.tsx)의 authHeaders 는 매 렌더마다 새 함수다. 그대로 의존성에 넣으면
   * load 의 정체성이 매번 바뀌고 → 폴링 useEffect 가 매 렌더 재실행되고 →
   * fetch 가 setState 를 부르며 무한 루프가 된다(실측: my-history 초당 4회).
   * ref 로 최신 함수만 들고 있고, 바깥에 내보내는 H 는 절대 바뀌지 않게 한다.
   */
  const hdrRef = useRef(authHeaders);
  hdrRef.current = authHeaders;
  const H = useCallback(() => hdrRef.current(), []);

  // ─────────────────────────────── 데이터
  const load = useCallback(async () => {
    try {
      const d = await fetch('/api/game/lobby', { headers: H() }).then(r => r.json());
      if (d.success) setLobby(d);
    } catch { /* 일시적 실패는 화면을 지우지 않는다 */ }
  }, [H]);

  const loadRank = useCallback(async () => {
    try {
      const [d, sn] = await Promise.all([
        fetch(`/api/game/ranking?scope=${rankScope}`, { headers: H() }).then(r => r.json()),
        fetch('/api/game/season', { headers: H() }).then(r => r.json()),
      ]);
      if (d.success) setRank(d);
      if (sn.success) setSeason(sn);
    } catch { /* noop */ }
  }, [H, rankScope]);

  const loadMissions = useCallback(async () => {
    try {
      const d = await fetch('/api/game/missions', { headers: H() }).then(r => r.json());
      if (d.success) setMissions(d.missions || []);
    } catch { /* noop */ }
  }, [H]);

  /**
   * 연속 참여 보너스 — 눌러서 받는다. (2026-10-17)
   * H()·load()·loadRoyale() 보다 뒤에 둬야 한다. 위에 뒀더니 초기화 전
   * 참조(TDZ)로 게임 화면이 통째로 안 떴다.
   */
  const loadStreak = useCallback(() => {
    // [2026-10-22] 출석은 달력·연속일수·랜덤박스를 같이 받는다
    fetch('/api/game/checkin', { headers: H() })
      .then(r => r.json()).then(j => { if (j?.success) setStreakInfo(j); })
      .catch(() => {});
  }, [H]);
  useEffect(() => { loadStreak(); }, [loadStreak]);

  const loadMyLog = useCallback(() => {
    fetch('/api/game/my-log?limit=60', { headers: H() })
      .then(r => r.json()).then(j => { if (j?.success) setMyLog(j); })
      .catch(() => {});
  }, [H]);

  const loadRoyale = useCallback(async () => {
    try {
      const d = await fetch('/api/game/royale', { headers: H() }).then(r => r.json());
      if (d.success) setRoyale(d);
    } catch { /* noop */ }
  }, [H]);

  const loadCoins = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        fetch('/api/game/coins', { headers: H() }).then(x => x.json()),
        fetch('/api/game/streak-run', { headers: H() }).then(x => x.json()),
      ]);
      if (c.success) setCoins(c);
      if (r.success) setSrun(r);
    } catch { /* noop */ }
  }, [H]);

  const loadShop = useCallback(async () => {
    try {
      const d = await fetch('/api/game/shop', { headers: H() }).then(r => r.json());
      if (d.success) setShop(d);
    } catch { /* noop */ }
  }, [H]);

  const loadHva = useCallback(async () => {
    try {
      const d = await fetch('/api/game/humanai', { headers: H() }).then(r => r.json());
      if (d.success) setHva(d);
    } catch { /* noop */ }
  }, [H]);

  const loadClan = useCallback(async () => {
    try {
      const [w, l] = await Promise.all([
        fetch('/api/game/clan-war', { headers: H() }).then(r => r.json()),
        fetch('/api/game/clans', { headers: H() }).then(r => r.json()),
      ]);
      if (w.success) setClanWar(w);
      if (l.success) setClanList(l);
    } catch { /* noop */ }
  }, [H]);

  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [load]);
  // 코인·연승은 보스 화면에서 자주 바뀐다
  useEffect(() => {
    loadCoins();
    const t = setInterval(loadCoins, 5000);
    return () => clearInterval(t);
  }, [loadCoins]);
  useEffect(() => { if (view === 'shop') loadShop(); }, [view, loadShop]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 250); return () => clearInterval(t); }, []);

  /**
   * 마감이 다가오면 짧게 진동으로 알린다(10초, 3·2·1초).
   * 같은 초에 두 번 울리지 않게 마지막으로 울린 초를 기억한다.
   * 진동을 막아둔 기기에서는 아무 일도 일어나지 않는다.
   */
  useEffect(() => {
    if (view !== 'royale') return;
    const left = secsTo(royale?.round?.locks_at);
    if (left <= 0 || left > 10) return;
    if (buzzed.current === left) return;
    if (left === 10 || left <= 3) {
      buzzed.current = left;
      try { navigator.vibrate?.(left === 10 ? 120 : 60); } catch { /* 막힌 기기 */ }
    }
  }, [now, view, royale?.round?.locks_at]);
  useEffect(() => { if (view === 'rank') loadRank(); }, [view, loadRank]);
  useEffect(() => {
    if (view === 'mission') { loadStreak(); loadMyLog(); }
  }, [view, loadStreak, loadMyLog]);
  useEffect(() => {
    if (view !== 'royale') return;
    loadRoyale();
    const t = setInterval(loadRoyale, 3000);
    return () => clearInterval(t);
  }, [view, loadRoyale]);

  useEffect(() => {
    if (view !== 'lobby') return;
    loadHva();            // 로비 배지에 실제 상태를 쓰려고 한 번 읽는다
  }, [view, loadHva]);

  useEffect(() => {
    if (view !== 'humanai') return;
    loadHva();
    const t = setInterval(loadHva, 3000);
    return () => clearInterval(t);
  }, [view, loadHva]);

  // 클랜 점수는 서버에서 10초에 한 번만 다시 세니 여기서도 10초면 충분하다
  useEffect(() => {
    if (view !== 'clan') return;
    loadClan();
    const t = setInterval(loadClan, 10000);
    return () => clearInterval(t);
  }, [view, loadClan]);

  const boss = lobby?.boss;
  const round = boss?.round;
  const prof = lobby?.profile;

  /**
   * 라운드가 넘어가는 순간을 잡아 결과를 크게 띄운다.
   * 지금까지는 숫자만 조용히 바뀌어서 이겼는지 졌는지도 몰랐다.
   */
  useEffect(() => {
    if (!round?.id) return;
    if (seenRound.current !== null && seenRound.current !== round.id) {
      loadMissions();
      const before = prevHp.current;
      const after = Number(boss?.hp ?? 0);
      const dealt = before !== null ? Math.max(0, before - after) : 0;
      if (dealt > 0) {
        setFlash({ dealt, cleared: after <= 0 });
        setTimeout(() => setFlash(null), 2600);
      }
    }
    seenRound.current = round.id;
    prevHp.current = Number(boss?.hp ?? 0);
  }, [round?.id, boss?.hp, loadMissions]);

  const left = useMemo(() => secsTo(round?.locksAt), [round?.locksAt, now]);
  const bossLeft = useMemo(() => secsTo(boss?.endsAt), [boss?.endsAt, now]);
  const royaleLeft = useMemo(() => secsTo(lobby?.royale?.startsAt), [lobby?.royale?.startsAt, now]);

  // ─────────────────────────────── 액션
  async function predict(choice: 'up' | 'down', roundId?: number) {
    const rid = roundId ?? round?.id;
    if (!rid || busy) return;
    setBusy(true);
    try {
      const d = await fetch('/api/game/predict', {
        method: 'POST',
        headers: { ...H(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ roundId: rid, choice: choice.toUpperCase(), stake }),
      }).then(r => r.json());
      if (d.success) {
        const nowP = Number(lobby?.price || 0);
        if (nowP) setMark({ t: Date.now(), p: nowP, dir: choice });
        toast.success(
          `${choice === 'up' ? '상승' : '하락'}에 베팅! ${stake > 1 ? `확신도 ×${stake}` : ''}`);
        load(); loadRoyale(); loadCoins();
      } else toast.error(d.error || '예측에 실패했습니다');
    } catch { toast.error('네트워크 오류'); } finally { setBusy(false); }
  }


  // 새 라운드가 오면 지난 선택 표시를 지운다. 안 지우면 남의 라운드에 점이 남는다.
  const roundKey = royale?.round?.id ?? null;
  useEffect(() => { setMark(null); }, [roundKey]);

  /**
   * 금액 기본값. 0 이면 버튼이 잠겨서 아무것도 못 한다 —
   * 실제로 그래서 베팅이 안 됐다. 처음 들어오면 알아서 채워 넣는다.
   */
  useEffect(() => {
    if (betAmt > 0) return;
    const w = Number(royale?.wallet?.balance || 0);
    if (w <= 0) return;
    const L = royale?.limits;
    const min = Number(L?.min || 100);
    const max = Number(L?.max || 10000);
    // 기본 400 wHANDY. 없으면 가진 만큼.
    setBetAmt(Math.max(min, Math.min(max, Math.min(w, 400))));
    /* eslint-disable-next-line */
  }, [royale?.wallet?.balance, royale?.limits]);

  /**
   * 라운드가 넘어가는 순간을 잡아 결과를 크게 띄운다.
   * 3초마다 읽는 값을 직전 것과 비교한다 — 라운드 번호가 올라갔으면 한 판이 끝난 것이다.
   */
  useEffect(() => {
    if (!royale?.session) return;
    const curRound = Number(royale.session.current_round || 0);
    const aliveNow = Number(royale.humans?.alive ?? 0) + Number(royale.bots?.alive ?? 0);
    const myAlive = Boolean(royale.me?.alive);
    const sid = Number(royale.session.id || 0);
    const prev = prevRoyale.current;
    prevRoyale.current = { round: curRound, alive: aliveNow, myAlive, sid };

    // 판 자체가 바뀌었으면 아무것도 띄우지 않는다.
    // 새 판 R1 은 지난 판 결과와 무관한데 "생존!" 이 스쳐 지나갔다.
    if (prev && (prev as any).sid !== sid) return;
    if (!prev || curRound <= prev.round) return;      // 아직 같은 라운드

    /**
     * [2026-10-22] 인원 숫자를 빼고 내 결과만 띄운다.
     * 여기 쓰던 alive 는 «실제» 수인데 화면의 참여자 칸은 표시용 값이라
     * "3명 탈락" 과 "36/56" 이 서로 안 맞아 보였다. 판이 리셋되는 순간엔
     * 특히 엉뚱한 수가 스쳐 지나갔다.
     */
    if (prev.myAlive && !myAlive) {
      setRoyFlash({ t: '💀 탈락했습니다', d: `R${prev.round} 에서 떨어졌습니다. 다음 판을 기다려주세요.`, tone: 'lose' });
    } else if (myAlive) {
      setRoyFlash({ t: '✅ 생존!', d: `R${prev.round} 통과 — 다음 라운드로 갑니다`, tone: 'win' });
    }
    // 참가하지 않았으면 아무것도 띄우지 않는다(구경하는 사람에게 숫자만 스쳤다)
    const t = setTimeout(() => setRoyFlash(null), 6000);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [royale?.session?.current_round, royale?.humans?.alive, royale?.bots?.alive, royale?.me?.alive]);

  /**
   * 서버에 남긴 베팅가로 만든 점.
   * 앱을 껐다 켜면 화면 메모리의 점(mark)은 사라지지만 이건 남는다.
   * 시각은 서버가 안 주므로 라운드 시작 시각으로 놓는다 — 대략의 위치면 충분하다.
   */
  const serverMark: Mark = (() => {
    const meR = royale?.me, rd = royale?.round;
    if (!meR?.myChoice || !meR?.myBetPrice || !rd) return null;
    const t = rd.opens_at ? new Date(rd.opens_at).getTime()
            : rd.locks_at ? new Date(rd.locks_at).getTime() - 20_000
            : Date.now() - 30_000;
    return { t, p: Number(meR.myBetPrice), dir: UP(meR.myChoice) ? 'up' : 'down' };
  })();

  /** 로얄 베팅 — 금액과 배수를 함께 보낸다. */
  async function betRoyale(choice: 'up' | 'down', roundId: number) {
    const w = Number(royale?.wallet?.balance || 0);
    const L = royale?.limits;
    const amt = Number(betAmt || 0);
    if (!amt) return toast.error('걸 금액을 입력해주세요');
    if (L && amt < Number(L.min)) return toast.error(`최소 ${fmt(L.min)} wHANDY 부터 걸 수 있습니다`);
    if (L && amt > Number(L.max)) return toast.error(`한 번에 ${fmt(L.max)} wHANDY 까지 걸 수 있습니다`);
    if (amt > w) return toast.error('wHANDY 가 부족합니다');
    if (busy) return;

    setBusy(true);
    try {
      // 참가를 안 했으면 여기서 같이 해준다.
      // joined 는 화면 그리는 쪽 변수라 여기서는 안 보인다 — 상태에서 직접 본다.
      if (!royale?.me) {
        const j = await fetch('/api/game/royale/join', { method: 'POST', headers: H() })
          .then(r => r.json());
        if (!j.success) throw new Error(j.error || '참가에 실패했습니다');
      }
      const d = await fetch('/api/game/predict', {
        method: 'POST',
        headers: { ...H(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ roundId, choice: choice.toUpperCase(), amount: amt, leverage: lev }),
      }).then(r => r.json());
      if (!d.success) throw new Error(d.error || '베팅에 실패했습니다');
      const nowP = Number(lobby?.price || 0);
      if (nowP) setMark({ t: Date.now(), p: nowP, dir: choice });
      toast.success(`${fmt(amt)} wHANDY · ${lev}× · ${choice === 'up' ? '오른다' : '내린다'}`);
      loadRoyale();
    } catch (e: any) {
      toast.error(e.message);
    } finally { setBusy(false); }
  }

  async function joinRoyale() {
    if (busy) return;
    setBusy(true);
    try {
      const d = await fetch('/api/game/royale/join', { method: 'POST', headers: H() })
        .then(r => r.json());
      if (d.success) { toast.success('로열에 참가했습니다!'); loadRoyale(); load(); }
      else toast.error(d.error || '참가에 실패했습니다');
    } catch { toast.error('네트워크 오류'); } finally { setBusy(false); }
  }

  async function claim(id: number) {
    if (busy) return;
    setBusy(true);
    try {
      const d = await fetch(`/api/game/missions/${id}/claim`, { method: 'POST', headers: H() })
        .then(r => r.json());
      if (d.success) { toast.success('보상을 받았습니다!'); loadMissions(); load(); }
      else toast.error(d.error || '아직 받을 수 없습니다');
    } catch { toast.error('네트워크 오류'); } finally { setBusy(false); }
  }

  async function srunAction(path: string) {
    if (busy) return;
    setBusy(true);
    try {
      const d = await fetch(`/api/game/streak-run/${path}`, { method: 'POST', headers: H() })
        .then(r => r.json());
      if (d.success) {
        toast.success(path === 'cash'
          ? `${fmt(d.payout)} 코인 획득! (${d.wins}연승 ×${d.mult})`
          : '연승 도전 시작! 틀리면 끝입니다');
        loadCoins();
      } else toast.error(d.error || '실패했습니다');
    } catch { toast.error('네트워크 오류'); } finally { setBusy(false); }
  }

  async function buyItem(id: number, name: string) {
    if (busy) return;
    setBusy(true);
    try {
      const d = await fetch(`/api/game/shop/${id}/buy`, { method: 'POST', headers: H() })
        .then(r => r.json());
      if (d.success) {
        toast.success(`${name} 교환 완료! 지급 대기열에 들어갔습니다`);
        loadShop(); loadCoins();
      } else toast.error(d.error || '교환에 실패했습니다');
    } catch { toast.error('네트워크 오류'); } finally { setBusy(false); }
  }

  async function claimSeason(id: number) {
    if (busy) return;
    setBusy(true);
    try {
      const d = await fetch(`/api/game/season/${id}/claim`, { method: 'POST', headers: H() })
        .then(r => r.json());
      if (d.success) { toast.success(`${fmt(d.reward)} wHANDY 지급 대기열에 넣었습니다`); loadRank(); }
      else toast.error(d.error || '받을 수 없습니다');
    } catch { toast.error('네트워크 오류'); } finally { setBusy(false); }
  }

  async function clanAction(url: string, body?: any) {
    if (busy) return;
    setBusy(true);
    try {
      const d = await fetch(url, {
        method: 'POST',
        headers: { ...H(), 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      }).then(r => r.json());
      if (d.success) { toast.success('처리했습니다'); setNewClan({ name: '', tag: '' }); loadClan(); }
      else toast.error(d.error || '실패했습니다');
    } catch { toast.error('네트워크 오류'); } finally { setBusy(false); }
  }

  // ─────────────────────────────── 공통 조각
  const Countdown = ({ sec, tone = 'orange' }: { sec: number; tone?: 'orange' | 'dark' }) => {
    const [h, m, s] = hms(sec);
    return (
      <div className={`ga-cd ga-cd-${tone}`}>
        {[[h, '시간'], [m, '분'], [s, '초']].map(([v, l], i) => (
          <div key={l} className="ga-cd-g">
            <div className="ga-cd-b">{v}</div>
            <div className="ga-cd-l">{l}</div>
            {i < 2 && <span className="ga-cd-sep">:</span>}
          </div>
        ))}
      </div>
    );
  };

  const Header = ({ title, sub }: { title: string; sub?: string }) => (
    <div className="ga-head">
      <div>
        <div className="ga-head-t">{title}</div>
        {sub && <div className="ga-head-s">{sub}</div>}
      </div>
      <div className="ga-head-me">
        <img src={`${A}/char-orange-wink.png?v=2`} alt="" />
        <span>👑 LV.{prof?.level ?? 1}</span>
      </div>
    </div>
  );

  const SecTitle = ({ children, more, onMore }: any) => (
    <div className="ga-sec">
      <h3>{children}</h3>
      {more && <button className="ga-more" onClick={onMore}>{more} ›</button>}
    </div>
  );

  // ══════════════════════════════════════════════ 로비 (ui03/ui08)
  const hvaBadge = !hva?.session ? '⏳ 준비 중'
    : hva.session.status === 'live' ? `● LIVE R${hva.session.current_round}/${hva.session.total_rounds}`
    : '🕐 곧 시작';

  const Lobby = () => (
    <>
      <Header title="예측하고, 경쟁하고," sub="더 큰 보상을 획득하세요!" />

      {/* 메인 히어로 배너 */}
      {/* 보스 히어로 배너 — 잠시 가림 (2026-10-01) */}
      {false && (
      <div className="ga-hero" onClick={() => setView('boss')}>
        <img src={`${A}/banner-arena.png?v=2`} alt="핸디 아레나" />
        <div className="ga-hero-foot">
          <span className="ga-live">● LIVE <b>{fmt(boss?.participants || 0)}명 참여 중</b></span>
          <button className="ga-btn-o">지금 입장하기 ›</button>
        </div>
      </div>
      )}

      {/* 지금 진행 중 — 3장 카드 */}
      <SecTitle more="전체보기" onMore={() => setView('rank')}>지금 진행 중</SecTitle>
      <div className="ga-cards3">
        {/* 배경을 가로배너로 깔면 세로칸에서 한글이 반쯤 잘린다(실제로 그랬다).
            글자 없는 그림만 가운데 놓고 제목은 CSS 로 쓴다. */}
        {/* 보스 카드 — 잠시 가림 (2026-10-01) */}
        {false && (
        <div className="ga-gc ga-gc-boss" onClick={() => setView('boss')}>
          <img className="ga-gc-art" src={`${A}/boss-golem.png?v=2`} alt="" />
          <div className="ga-gc-top">
            <span className="ga-chip-live">● LIVE</span>
            <span className="ga-chip-n">👥 {fmt(boss?.participants || 0)}명</span>
          </div>
          <div className="ga-gc-body">
            <div className="ga-gc-t">BTC 보스<br />레이드</div>
            <div className="ga-gc-d">함께 도전하고<br />보상을 획득하세요!</div>
          </div>
          <button className="ga-btn-o ga-gc-cta">참여하기 ›</button>
        </div>
        )}

        <div className="ga-gc ga-gc-royale" onClick={() => setView('royale')}>
          <img className="ga-gc-art ga-gc-art-s" src={`${A}/item-crown.png?v=2`} alt="" />
          <div className="ga-gc-top">
            <span className="ga-chip-n">👥 {fmt(lobby?.royale?.entrants || 0)}명</span>
          </div>
          <div className="ga-gc-body">
            <div className="ga-gc-t">HANDY<br />ROYALE</div>
            <div className="ga-gc-d">랭킹 TOP 10 에 들어<br />상금을 받으세요!</div>
            <Countdown sec={royaleLeft} tone="dark" />
          </div>
          <button className="ga-btn-b ga-gc-cta">자세히 보기 ›</button>
        </div>

        <div className="ga-gc ga-gc-pick" onClick={() => setView('mission')}>
          <img className="ga-gc-art ga-gc-art-s" src={`${A}/item-calendar10.png?v=2`} alt="" />
          <div className="ga-gc-top">
            <span className="ga-chip-n">👥 {fmt(prof?.totalPlayers || 0)}명</span>
          </div>
          <div className="ga-gc-body">
            <div className="ga-gc-t">오늘의<br />10 PICK</div>
            <div className="ga-gc-d">매일 10개의 예측으로<br />보상을 받아보세요!</div>
          </div>
          <div className="ga-streak">
            <div className="ga-streak-t">{prof?.attendStreak || 0}일 연속 참여 중!</div>
            <div className="ga-streak-d">
              {Array.from({ length: 7 }, (_, i) => (
                <span key={i} className={i < (prof?.attendStreak || 0) ? 'on' : ''}>
                  {i < (prof?.attendStreak || 0) ? '✓' : ''}
                </span>
              ))}
            </div>
          </div>
          <button className="ga-btn-o ga-gc-cta">참여하기 ›</button>
        </div>
      </div>

      {/* 곧 시작 */}
      <SecTitle>함께 하는 게임</SecTitle>
      {/* 이 배너들은 가로로 긴 그림이고 제목·설명이 이미 그려져 있다.
          2단으로 쪼개면 그림도 글자도 다 잘린다. 한 줄로 크게 보여준다. */}
      <div className="ga-wide2">
        <div className="ga-soon" onClick={() => setView('humanai')}>
          <img src={`${A}/banner-humanai.png?v=2`} alt="Human vs AI" />
          <span className="ga-soon-badge">{hvaBadge}</span>
          <button className="ga-btn-o ga-soon-cta"
            onClick={e => { e.stopPropagation(); setView('humanai'); }}>지금 참여 ›</button>
        </div>
        {/* 클랜 카드 — 잠시 가림 (2026-10-01) */}
        {false && (
        <div className="ga-soon" onClick={() => setView('clan')}>
          <img src={`${A}/banner-clan.png?v=2`} alt="클랜 대전" />
          <span className="ga-soon-badge">⚔ 상시</span>
          <button className="ga-btn-o ga-soon-cta"
            onClick={e => { e.stopPropagation(); setView('clan'); }}>클랜 보기 ›</button>
        </div>
        )}
      </div>

      {/* 내 아레나 기록 */}
      <SecTitle more="시즌 기록 보기" onMore={() => setView('rank')}>내 아레나 기록</SecTitle>
      <div className="ga-stats">
        <div className="ga-stats-badge" style={{ borderColor: leagueOf(prof?.league).color }}>
          <img src={`${A}/item-shield.png?v=2`} alt="" />
          <span style={{ color: leagueOf(prof?.league).color }}>{leagueOf(prof?.league).label}</span>
        </div>
        {[
          ['내 순위', `🥇 ${fmt(prof?.rank || 0)}위`],
          ['연속 적중', `💥 ${fmt(prof?.streak || 0)}회`],
          ['이번 주 승률', `📈 ${prof?.winRate ?? 0}%`],
          ['시즌 포인트', `⭐ ${fmt(prof?.season_points || 0)}`],
        ].map(([l, v]) => (
          <div key={l} className="ga-stat"><div className="ga-stat-l">{l}</div><div className="ga-stat-v">{v}</div></div>
        ))}
      </div>

      {/* 보상 풀 */}
      <SecTitle more="상점 열기" onMore={() => setView('shop')}>보상 풀</SecTitle>
      <div className="ga-pool">
        <img className="ga-pool-img" src={`${A}/item-coins.png?v=2`} alt="" />
        <div className="ga-pool-mid">
          <div className="ga-pool-l">💰 현재 누적 보상</div>
          <div className="ga-pool-v">{fmt(lobby?.royale?.prizePool || 0)}<small>wHANDY</small></div>
        </div>
        <div className="ga-pool-rank">
          <div className="ga-pool-rt">🥇 추가 랭킹 보상</div>
          <div className="ga-pool-rr"><span>한 판</span><b>10라운드</b></div>
          <div className="ga-pool-rr"><span>받는 사람</span><b>랭킹 TOP 10</b></div>
          <div className="ga-pool-rr"><span>지급</span><b>매일 자정</b></div>
        </div>
      </div>

    </>
  );

  // ══════════════════════════════════════════════ 보스 레이드 (ui04/ui05)
  const Boss = () => {
    const hpPct = boss?.hpPercent ?? 100;
    const up = Number(boss?.upDamage || 0), down = Number(boss?.downDamage || 0);
    const tot = up + down || 1;
    const upPct = Math.round(up / tot * 100);
    const locked = !!boss?.myPick;
    const stages = [20, 40, 60, 80, 100];
    const cleared = 100 - hpPct;
    const chests = [`${A}/chest-bronze.png?v=2`, `${A}/chest-bronze.png?v=2`, `${A}/chest-gold.png?v=2`,
                    `${A}/chest-bronze.png?v=2`, `${A}/chest-gold.png?v=2`];

    return (
      <>
        <div className="ga-back"><button onClick={() => setView('lobby')}>‹ 아레나</button></div>

        {/* 보스 비주얼 */}
        {flash && (
          <div className={`ga-flash ${flash.cleared ? 'cleared' : ''}`}>
            {flash.cleared ? (
              <>
                <div className="ga-flash-big">⚔ 보스 처치!</div>
                <div className="ga-flash-sub">더 강한 보스가 곧 등장합니다</div>
              </>
            ) : (
              <>
                <div className="ga-flash-big">-{fmt(flash.dealt)}</div>
                <div className="ga-flash-sub">
                  {boss?.myPick ? '이번 라운드 데미지!' : '지금 참여하면 함께 때릴 수 있어요'}
                </div>
              </>
            )}
          </div>
        )}

        {/* 배너 이미지에 제목·설명이 이미 그려져 있다. 위에 또 글자를 얹으면 겹쳐서
            둘 다 안 읽힌다(실제로 그랬다). 배지만 얹는다. */}
        <div className="ga-boss-hero">
          <img src={`${A}/banner-boss.png?v=2`} alt="BTC 보스 레이드" />
          <div className="ga-boss-live">
            <span className="ga-chip-live">● LIVE</span>
            <span className="ga-chip-n">👥 {fmt(boss?.participants || 0)}명 함께 전투 중</span>
          </div>
        </div>
        <div className="ga-ai-note">
          🤖 사람이 적은 시간대에는 <b>참여자</b>가 함께 보스를 공격합니다
        </div>

        {/* HP 바 */}
        <div className="ga-card ga-hp">
          <div className="ga-hp-head">
            <span>👹 {boss?.name || 'BTC 데스 골렘'}</span><span className="ga-dim">Lv. ???</span>
          </div>
          <div className="ga-hp-bar">
            <div className="ga-hp-fill" style={{ width: `${hpPct}%` }} />
            <span className="ga-hp-txt">{fmt(boss?.hp || 0)} / {fmt(boss?.maxHp || 0)} HP</span>
            <span className="ga-hp-pct">{hpPct}%</span>
          </div>
          <div className="ga-hp-hint">
            {hpPct <= 15 ? '🔥 막타 각! 지금 들어가면 처치 기여로 남습니다'
              : hpPct <= 50 ? '절반 넘겼습니다 — 조금만 더!'
              : '함께 때릴수록 빨리 잡힙니다'}
          </div>
        </div>

        {/* 남은 시간 / 콤보 / 누적 데미지 */}
        <div className="ga-row3">
          <div className="ga-card ga-mini">
            <div className="ga-mini-l">🕐 남은 시간</div>
            <div className="ga-mini-v">{hms(bossLeft).join(' : ')}</div>
          </div>
          <div className="ga-combo">
            <div className="ga-combo-l">연속 성공 시 데미지가 증가합니다!</div>
            <div className="ga-combo-v">COMBO {boss?.myDamage?.combo ?? 0}</div>
          </div>
          <div className="ga-card ga-mini">
            <div className="ga-mini-l">💥 내 누적 데미지</div>
            <div className="ga-mini-v">{fmt(boss?.myDamage?.damage || 0)}</div>
          </div>
        </div>

        {/* 확신도 — 지금까지는 UP/DOWN 둘 중 하나뿐이라 고민할 게 없었다 */}
        {!locked && (
          <div className="ga-stake">
            <div className="ga-stake-l">확신도를 고르세요 — 높을수록 데미지·코인이 커집니다</div>
            <div className="ga-stake-btns">
              {([[1, '살짝', '안전하게'], [3, '확신', '3배'], [10, '올인', '10배']] as const)
                .map(([v, t, d]) => (
                  <button key={v} className={`ga-stake-b s${v} ${stake === v ? 'on' : ''}`}
                    onClick={() => setStake(v as 1 | 3 | 10)}>
                    <b>{t}</b><small>{d}</small>
                  </button>
                ))}
            </div>
          </div>
        )}

        {/* 연승 도전 */}
        {srun && (
          srun.run ? (
            <div className="ga-srun live">
              <div className="ga-srun-h">
                ⚡ 연승 도전 <b>{srun.run.wins}연승</b>
                <span className="ga-srun-m">×{srun.run.mult}</span>
              </div>
              <div className="ga-srun-s">
                한 번만 틀려도 끝납니다 · 지금 받으면 <b>{fmt(srun.run.payout)}</b> 코인
              </div>
              <button className="ga-btn-o ga-sm2" disabled={busy || srun.run.wins < 1}
                onClick={() => srunAction('cash')}>
                {srun.run.wins < 1 ? '1승부터 받을 수 있어요' : '여기서 그만하고 받기'}
              </button>
            </div>
          ) : (
            <div className="ga-srun">
              <div className="ga-srun-h">⚡ 연승 도전</div>
              <div className="ga-srun-s">
                참가비 {fmt(srun.entryCoins)}코인 · 연승할수록 배수가 폭증합니다
                (3승 5배 · 5승 20배 · 7승 100배)
              </div>
              <button className="ga-btn-o ga-sm2"
                disabled={busy || (coins?.coins || 0) < srun.entryCoins}
                onClick={() => srunAction('start')}>
                {(coins?.coins || 0) < srun.entryCoins ? '코인이 부족합니다' : '도전 시작'}
              </button>
            </div>
          )
        )}

        {/* 오른다 / 내린다 */}
        <div className="ga-bet">
          <button
            className={`ga-bet-up ${UP(boss?.myPick) ? 'picked' : ''}`}
            disabled={locked || busy || left <= 0}
            onClick={() => predict('up')}
          >
            <div className="ga-bet-cap">BTC 가격이</div>
            <div className="ga-bet-big">⬆ 오른다</div>
            <div className="ga-bet-sub">
              적중 시 {fmt((500 + Math.min(boss?.myDamage?.combo || 0, 20) * 250) * stake)} 데미지
            </div>
          </button>
          <button
            className={`ga-bet-down ${DOWN(boss?.myPick) ? 'picked' : ''}`}
            disabled={locked || busy || left <= 0}
            onClick={() => predict('down')}
          >
            <div className="ga-bet-cap">BTC 가격이</div>
            <div className="ga-bet-big">⬇ 내린다</div>
            <div className="ga-bet-sub">
              적중 시 {fmt((500 + Math.min(boss?.myDamage?.combo || 0, 20) * 250) * stake)} 데미지
            </div>
          </button>
        </div>
        {/* 예측을 걸어둔 60초가 제일 심심했다. 지금 이기고 있는지를 계속 보여준다. */}
        {locked ? (() => {
          const now = Number(lobby?.price || 0);
          const open = Number(round?.openPrice || 0);
          const diff = open ? (now - open) / open * 100 : 0;
          const winning = UP(boss.myPick) ? diff > 0 : diff < 0;
          return (
            <div className={`ga-live-note ${winning ? 'win' : 'lose'}`}>
              <div className="ga-live-t">
                {winning ? '🔥 이기는 중!' : '😬 지고 있어요'}
                <b>{diff >= 0 ? '+' : ''}{diff.toFixed(3)}%</b>
              </div>
              <div className="ga-live-s">
                내 선택 <b>{UP(boss.myPick) ? '⬆ 상승' : '⬇ 하락'}</b> · 결과까지 <b>{mmss(left)}</b>
              </div>
            </div>
          );
        })() : (
          <div className="ga-round-note">
            ROUND {round?.no} · 마감까지 <b>{mmss(left)}</b> · 현재가 <b>${fmt(lobby?.price || 0, 2)}</b>
          </div>
        )}

        {/* 팀별 누적 데미지 */}
        <div className="ga-card">
          <div className="ga-card-t">👥 팀별 누적 데미지</div>
          <div className="ga-team">
            <div><span className="ga-up">⬆ 상승팀</span><b>{fmt(up)} <small>({upPct}%)</small></b></div>
            <div className="ga-r"><span className="ga-down">하락팀 ⬇</span><b>{fmt(down)} <small>({100 - upPct}%)</small></b></div>
          </div>
          <div className="ga-team-bar"><div style={{ width: `${upPct}%` }} /></div>
        </div>

        {/* 레이드 보상 단계 */}
        <div className="ga-card">
          <div className="ga-card-t">🎁 레이드 보상 단계</div>
          <div className="ga-stage">
            <div className="ga-stage-line"><div style={{ width: `${Math.min(100, cleared)}%` }} /></div>
            {stages.map((s, i) => (
              <div key={s} className={`ga-stage-i ${cleared >= s ? 'done' : cleared >= s - 20 ? 'now' : ''}`}>
                <img src={chests[i]} alt="" />
                <div className="ga-stage-p">{s}%</div>
                <div className="ga-stage-s">
                  {cleared >= s ? '달성 완료' : cleared >= s - 20 ? '진행 중' : '대기 중'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 획득 가능 보상 */}
        <div className="ga-card ga-reward">
          <img src={`${A}/item-coins.png?v=2`} alt="" />
          <div>
            <div className="ga-card-t">획득 가능 보상</div>
            <div className="ga-reward-v">+ 100 ~ 1,000 wHANDY</div>
            <div className="ga-dim ga-sm">기여도에 따라 보상이 지급됩니다.</div>
          </div>
        </div>

        {/* 실시간 레이드 로그 — 내 최근 예측이 곧 로그다 */}
        <div className="ga-card">
          <div className="ga-card-t">📜 내 레이드 기록</div>
          <BossLog authHeaders={H} roundId={round?.id} />
        </div>

        <ChatBox authHeaders={H} room={`boss:${boss?.id || 1}`} title="💬 실시간 레이드 채팅" />
      </>
    );
  };

  // ══════════════════════════════════════════════ 로열 (ui09/ui12)
  const Royale = () => {
    const s = royale?.session, r = royale?.round, me = royale?.me;
    // [2026-10-21] 베팅 마감(locks_at)과 판정(resolves_at)이 이제 다른 시각이다.
    // rLeft 는 «걸 수 있는 시간», jLeft 는 «결과 나올 때까지».
    const rLeft = secsTo(r?.locks_at);
    const jLeft = secsTo(r?.resolves_at);
    const betWin = r?.opens_at && r?.locks_at
      ? Math.max(1, Math.round((new Date(r.locks_at).getTime() - new Date(r.opens_at).getTime()) / 1000))
      : 20;
    const judgeWin = r?.locks_at && r?.resolves_at
      ? Math.max(1, Math.round((new Date(r.resolves_at).getTime() - new Date(r.locks_at).getTime()) / 1000))
      : 40;
    const betOpen = rLeft > 0;
    const tLeft = betOpen ? rLeft : jLeft;                         // 화면에 띄울 남은 시간
    const tPct = betOpen ? (rLeft / betWin) : (jLeft / judgeWin);   // 진행바
    const startLeft = secsTo(s?.starts_at);
    const joined = !!me;
    const alive = me?.alive !== false;
    const bots = royale?.bots || { total: 0, alive: 0 };
    const h0 = royale?.humans || { total: s?.entrants || 0, alive: s?.survivors || 0 };
    // 화면에 띄우는 참여자 수는 서버가 주는 표시용 값을 쓴다. 상금·정산은
    // 서버가 실제 수로 계산하므로 이 숫자가 돈에 닿지 않는다. (2026-10-16)
    const humans = { total: h0.shownTotal ?? h0.total, alive: h0.shownAlive ?? h0.alive };
    const live = s?.status === 'live';
    // 진행중에도 들어갈 수 있다(입장). 지나간 라운드를 안 겪었으니 상금만 빠진다.
    const canLate = !joined && live;

    const capNow = royale?.dailyCap || cap;

    return (
      <>
        {/* 오늘 쏘는 금액 — 들어오면 바로 보이게 맨 위에 둔다 (2026-10-16) */}
        {/* 하루 한도 배너 — 배너가 먼저 보이게 상단에서 내렸다 (2026-10-17) */}
        {false && (
          <div style={{
            margin: '0 0 10px', padding: '12px 14px', borderRadius: 14,
            background: capNow.closed
              ? 'linear-gradient(135deg,#3a3a44,#24242b)'
              : 'linear-gradient(135deg,#FF6B00,#FF9500)',
            color: '#fff', boxShadow: '0 4px 14px rgba(0,0,0,.18)',
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 15, fontWeight: 800 }}>
                🎯 핸디로얄이 매일 ${Number(capNow.limitUsd || 0).toLocaleString('en-US')} 을 쏩니다
              </span>
            </div>

            {capNow.closed ? (
              <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.6, opacity: .95 }}>
                오늘 쏘는 금액이 모두 지급됐습니다.<br />
                <b>내일을 기대해주세요! 매일 선착순입니다.</b>
              </div>
            ) : (
              <>
                <div style={{
                  height: 8, borderRadius: 99, marginTop: 9,
                  background: 'rgba(255,255,255,.28)', overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${Math.max(2, Number(capNow.percent || 0))}%`, height: '100%',
                    borderRadius: 99, background: '#fff', transition: 'width .4s',
                  }} />
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontSize: 12, marginTop: 6, opacity: .95, fontWeight: 600,
                }}>
                  <span>오늘 ${Math.round(Number(capNow.paidUsd || 0)).toLocaleString('en-US')} 지급</span>
                  <span>남은 금액 ${Math.round(Number(capNow.remainingUsd || 0)).toLocaleString('en-US')}</span>
                </div>
                <div style={{ fontSize: 12, marginTop: 5, opacity: .9 }}>
                  매일 선착순 — 먼저 참여한 분께 먼저 지급됩니다.
                </div>
              </>
            )}
          </div>
        )}

        {/* 마감 팝업 — 다 쏜 날은 화면을 덮어 확실히 알린다 */}
        {capNow?.closed && !capClosedSeen && (
          <div
            onClick={() => setCapClosedSeen(true)}
            style={{
              position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.62)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22,
            }}>
            <div
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 340, borderRadius: 18, padding: '26px 22px',
                background: '#fff', color: '#17171c', textAlign: 'center',
                boxShadow: '0 18px 50px rgba(0,0,0,.35)',
              }}>
              <div style={{ fontSize: 40, lineHeight: 1 }}>🎉</div>
              <div style={{ fontSize: 19, fontWeight: 800, marginTop: 12 }}>
                오늘 게임은 종료됐습니다
              </div>
              <div style={{ fontSize: 14, marginTop: 10, lineHeight: 1.75, opacity: .8 }}>
                오늘 쏘는 <b>${Number(capNow.limitUsd || 0).toLocaleString('en-US')}</b> 이
                모두 지급됐습니다.<br />
                <b style={{ color: '#FF6B00' }}>내일을 기대해주세요!</b><br />
                핸디로얄은 매일 선착순입니다.
              </div>
              <button
                onClick={() => setCapClosedSeen(true)}
                style={{
                  width: '100%', marginTop: 20, padding: 14, border: 0, borderRadius: 12,
                  background: '#FF6B00', color: '#fff', fontWeight: 800, fontSize: 15,
                  cursor: 'pointer',
                }}>
                확인
              </button>
            </div>
          </div>
        )}

        {/* 등수별 상금 — 비율만 보여주면 얼마 받는지 감이 안 온다.
            실제 코인 수량으로 띄운다. (2026-10-16) */}
        {/* 이 판 상금 TOP 10 — 배너가 먼저 보이게 상단에서 내렸다 (2026-10-17) */}
        {false && (
          <div className="ga-card" style={{ padding: 14, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'baseline', marginBottom: 2 }}>
              <span style={{ fontWeight: 800, fontSize: 15 }}>🏆 이 판 상금 TOP 10</span>
              <span style={{ fontSize: 12, opacity: .7 }}>등수대로 100% 분배</span>
            </div>
            <div style={{ fontSize: 13, opacity: .75, marginBottom: 10 }}>
              총 상금 <b style={{ opacity: 1 }}>{fmt(royale.prizeSplit.prize || 0)} wHANDY</b>
              {' · '}참여자 {fmt(humans.total)}명
            </div>

            {royale.prizeSplit.plan.map((row: any) => (
              <div key={row.rank} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 0',
                borderTop: row.rank === 1 ? 0 : '1px solid rgba(128,128,128,.14)',
                opacity: row.filled ? 1 : .45,
              }}>
                <span style={{
                  width: 26, height: 26, flexShrink: 0, borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 800, color: '#fff',
                  background: row.rank === 1 ? '#F5A524'
                    : row.rank === 2 ? '#9AA3AE'
                    : row.rank === 3 ? '#C77B3C' : 'rgba(128,128,128,.5)',
                }}>{row.rank}</span>
                <span style={{ fontSize: 13, opacity: .7, width: 40, flexShrink: 0 }}>
                  {row.percent}%
                </span>
                <span style={{
                  flex: 1, minWidth: 0, fontSize: 13, fontWeight: row.mine ? 800 : 600,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  letterSpacing: .3,
                  color: row.mine ? '#FF6B00' : undefined,
                }}>
                  {row.name || '—'}{row.mine ? ' (나)' : ''}
                </span>
                <span style={{ textAlign: 'right', fontWeight: 800, fontSize: 15 }}>
                  {fmt(row.amount || 0)}
                  <span style={{ fontSize: 11, fontWeight: 600, opacity: .6 }}> wHANDY</span>
                </span>
              </div>
            ))}

            <div style={{ fontSize: 12, opacity: .65, marginTop: 9 }}>
              매일 자정에 등수대로 자동 지급됩니다.
            </div>
          </div>
        )}

        {/* 소각 내역 — "재단 수익의 80% 를 매일 태웁니다" 를 숫자로 보여준다 */}
        {/* 소각 내역 — 배너가 먼저 보이게 상단에서 내렸다 (2026-10-17) */}
        {false && (
          <div className="ga-card" style={{ padding: 14, marginBottom: 10 }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 2 }}>
              🔥 소각 내역
            </div>
            <div style={{
              padding: '10px 12px', borderRadius: 12,
              background: 'rgba(255,107,0,.10)', marginBottom: 10,
            }}>
              <div style={{ fontSize: 11, opacity: .7 }}>누적 소각</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#FF6B00' }}>
                {fmt(Math.max(0, Number(burns.net_burn || 0)))}
                <span style={{ fontSize: 11, fontWeight: 600, opacity: .7 }}> wHANDY</span>
              </div>
            </div>

            {(burns.rows || []).length === 0 ? (
              <div style={{ fontSize: 13, opacity: .6, padding: '10px 0' }}>
                매일 자정에 소각하고 여기에 올립니다.
              </div>
            ) : (
              (burns.rows || []).map((r: any) => (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0',
                  borderTop: '1px solid rgba(128,128,128,.14)', fontSize: 13,
                }}>
                  <span style={{ opacity: .7, width: 84, flexShrink: 0 }}>{r.day}</span>
                  <span style={{ flex: 1, fontWeight: 700,
                                 color: Number(r.net) > 0 ? '#FF6B00' : 'inherit' }}>
                    {r.skipped ? '태울 수익 없음' : `${fmt(Math.round(Number(r.net)))} 소각`}
                  </span>
                  {r.tx_hash && (
                    <span style={{ fontSize: 11, opacity: .55 }}>
                      {String(r.tx_hash).slice(0, 10)}…
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* 매입 프로그램 랜딩 — 현재 미운영이라 가림 (2026-10-01) */}
        {false && (<>
        <div className="ga-back"><button onClick={() => setView('lobby')}>‹ 아레나</button></div>

        {/* 핸디로얄 — 랜딩 → 참여하기 → (아래) 게임 (2026-09-18)
            접어두면 대부분 안 펼친다. 펼쳐서 보여주고 바로 아래 참여 버튼을 둔다. */}
        <div className="ga-card" style={{ padding: 0, overflow: 'hidden' }}>
          <picture>
            <source srcSet="/royal/royal-banner.webp?v=2" type="image/webp" />
            <img
              src="/royal/royal-banner.jpg?v=2"
              alt="Handy Royal — HANDY 매입 프로그램"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </picture>

          {/* 참여 — 랜딩과 게임 사이에 둔다 */}
          <div style={{ padding: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
              👑 HANDY 매입 프로그램
            </div>
            <div style={{ fontSize: 13, opacity: .8, lineHeight: 1.7, marginBottom: 12 }}>
              보유한 HANDY 를 맡기면 <b>240%</b> 를 <b>365일</b> 동안 매일 나눠 받습니다.
              <br />보상은 <b>wHANDY</b> 또는 <b>USDT</b> 중에 고르세요.
            </div>
            <button
              onClick={() => { try { window.dispatchEvent(new CustomEvent('handy:open-royal')); } catch {} }}
              style={{ width: '100%', padding: 15, border: 0, borderRadius: 12,
                       background: '#FF6B00', color: '#fff', fontWeight: 800, fontSize: 16 }}>
              지금 참여하기 ›
            </button>

            <button
              onClick={() => setRoyalInfo(v => !v)}
              style={{
                width: '100%', marginTop: 8, padding: 10, background: 'transparent',
                border: 0, color: 'inherit', opacity: .65, fontSize: 13, cursor: 'pointer',
              }}>
              {royalInfo ? '프로그램 설명 접기 ▲' : '프로그램 설명 더 보기 ▼'}
            </button>
          </div>

          {royalInfo && (
            <picture>
              <source srcSet="/royal/royal-steps.webp?v=2" type="image/webp" />
              <img
                src="/royal/royal-steps.jpg?v=2"
                alt="HANDY 매입 프로그램 · 거래소 매입 · 365일 데일리 지급 · 소각 발표"
                style={{ width: '100%', height: 'auto', display: 'block' }}
                loading="lazy"
              />
            </picture>
          )}
        </div>

        </>)}

        <div className="ga-roy-hero">
          <img src={`${A}/banner-royale.png?v=2`} alt="HANDY ROYALE" />
        </div>

        <div className="ga-card ga-roy-cd">
          <div className="ga-roy-cd-l">
            {s?.status === 'running' ? '다음 라운드까지' : '다음 라운드 시작까지'}
          </div>
          <Countdown sec={s?.status === 'running' ? tLeft : startLeft} />
        </div>

        {royFlash && (
          <div style={{
            margin: '0 0 10px', padding: '14px 16px', borderRadius: 14, textAlign: 'center',
            background: royFlash.tone === 'win' ? 'rgba(34,197,94,.18)'
              : royFlash.tone === 'lose' ? 'rgba(239,68,68,.18)' : 'rgba(156,163,175,.16)',
            border: `1px solid ${royFlash.tone === 'win' ? '#22C55E' : royFlash.tone === 'lose' ? '#EF4444' : '#9CA3AF'}66`,
          }}>
            <div style={{ fontSize: 19, fontWeight: 900 }}>{royFlash.t}</div>
            <div style={{ fontSize: 13, opacity: .85, marginTop: 3 }}>{royFlash.d}</div>
          </div>
        )}

        {joined && alive && Number(s?.current_round || 0) > Number(me?.joined_round || 0) && (
          <div style={{
            margin: '0 0 10px', padding: '9px 12px', borderRadius: 12, fontSize: 13,
            background: 'rgba(255,107,0,.14)', border: '1px solid rgba(255,107,0,.35)',
          }}>
            🔥 <b>{Number(s?.current_round || 0) - Number(me?.joined_round || 0)}라운드 연속 생존</b> 중입니다
          </div>
        )}

        <div className="ga-roy-stats">
          {[
            { I: Users,  l: '참여자', v: `${fmt(humans.alive + bots.alive)}/${fmt(humans.total + bots.total)}` },
            { I: RotateCw, l: '라운드', v: `${s?.current_round || 0}/${s?.total_rounds || 10}` },
            { I: Trophy, l: '총 상금', v: fmt(Number(royale?.pool?.prize || 0), 0) },
          ].map(({ I, l, v }) => (
            <div key={l} className="ga-roy-stat">
              <div className="ga-roy-stat-l"
                   style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                <I size={14} /> {l}
              </div>
              <div className="ga-roy-stat-v">{v}</div>
            </div>
          ))}
        </div>

        {/* 지갑에 wHANDY 가 없을 때만. 이미 있는 사람에게 넣으라고 하지 않는다. */}
        {Number(royale?.wallet?.balance || 0) <= 0 && (
          <div className="ga-card" style={{
            border: '1px solid rgba(255,107,0,.5)', background: 'rgba(255,107,0,.10)',
          }}>
            <div style={{ fontWeight: 900, fontSize: 17, marginBottom: 6 }}>
              wHANDY 가 없습니다
            </div>
            <div style={{ fontSize: 13, opacity: .85, lineHeight: 1.7, marginBottom: 12 }}>
              베팅은 지갑의 wHANDY 로 합니다.
              <br />맞히면 <b>건 돈 × 배수 × {royale?.limits?.payoutMul ?? 1.9}</b> 를 돌려받고,
              틀리면 건 돈을 잃습니다.
            </div>
            <button
              className="ga-btn-o ga-wide"
              onClick={() => { try { window.dispatchEvent(new CustomEvent('handy:open-royal')); } catch {} }}
            >
              💰 HANDY 사서 참여하기 ›
            </button>
          </div>
        )}

        {/* 내 지갑 — 이게 곧 베팅 자산이다 */}
        <div className="ga-card" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
            <span style={{
              width: 42, height: 42, borderRadius: 12, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,107,0,.16)', color: '#FF6B00',
            }}><Wallet size={22} /></span>
            <div>
              <div style={{ fontSize: 12, opacity: .7 }}>내 wHANDY</div>
              <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: -.4, lineHeight: 1.15 }}>
                {fmt(Number(royale?.wallet?.balance || 0), 2)}
              </div>
              {(() => {
                const pf = Number(royale?.todayProfit || 0);
                const bet0 = Number(royale?.todayBetTotal || 0);
                // 기준이 '베팅액' 인 비율을 잔액 바로 밑에 붙여놔서 자산이 30%
                // 날아간 것처럼 읽혔다(실제로는 -2.1%). 무슨 기준인지 같이 쓴다.
                const rate = bet0 > 0 ? (pf / bet0) * 100 : 0;
                const up = pf > 0, down = pf < 0;
                const Ico = up ? TrendingUp : TrendingDown;
                return (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, marginTop: 3,
                    fontWeight: 700,
                    color: up ? '#22C55E' : down ? '#EF4444' : undefined, opacity: pf ? 1 : .55,
                  }}>
                    {pf ? <Ico size={14} /> : null}
                    오늘 {pf >= 0 ? '+' : ''}{fmt(pf, 0)}
                    {bet0 > 0 ? (
                      <span style={{ fontWeight: 600, opacity: .8 }}>
                        {` · 베팅 ${fmt(bet0, 0)} 대비 ${rate >= 0 ? '+' : ''}${rate.toFixed(1)}%`}
                      </span>
                    ) : null}
                  </div>
                );
              })()}
            </div>
          </div>
          {/* [2026-10-22] 룰렛은 상단 탭으로 갔다. 여기 작은 버튼은 가린다. */}
          {false && <button
            onClick={() => setView('wheel' as any)}
            title="무료 참여 룰렛"
            style={{
              width: 46, height: 46, borderRadius: 14, border: 0, flexShrink: 0, cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              background: Number(royale?.spin?.left || 0) > 0
                ? 'linear-gradient(180deg,#FFB020,#FF6B00)' : 'rgba(255,255,255,.10)',
              color: '#fff',
            }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>🎡</span>
            {royale?.spin && (
              <span style={{ fontSize: 10, fontWeight: 800, marginTop: 1 }}>
                {Number(royale.spin.used || 0)}/{Number(royale.spin.perDay || 3)}
              </span>
            )}
          </button>}
          {/* 이력은 미션 탭의 '참여 이력' 으로 옮겼다 */}
          {false && <button
            onClick={() => setShowLedger(v => !v)}
            style={{
              padding: '8px 12px', borderRadius: 10, border: 0,
              background: 'rgba(255,255,255,.10)', color: 'inherit', fontSize: 13, cursor: 'pointer',
            }}>
            이력 {showLedger ? '▲' : '▼'}
          </button>}
        </div>

        {showLedger && (
          <div className="ga-card" style={{ maxHeight: 260, overflow: 'auto' }}>
            {(royale?.ledger || []).length === 0 ? (
              <div className="ga-dim ga-sm">아직 기록이 없습니다.</div>
            ) : (royale.ledger as any[]).map(x => (
              <div key={x.id} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0',
                borderBottom: '1px solid rgba(255,255,255,.07)', fontSize: 13,
              }}>
                <span style={{ opacity: .8 }}>
                  {x.kind === 'bet' ? '🎯 베팅' : x.kind === 'win' ? '🏆 적중'
                    : x.kind === 'prize' ? '👑 1등 상금' : x.kind === 'deposit' ? '💰 충전' : '↗ 출금'}
                  {x.leverage ? ` ${x.leverage}×` : ''}
                </span>
                <b style={{ marginLeft: 'auto', color: Number(x.amount) >= 0 ? '#22C55E' : '#EF4444' }}>
                  {Number(x.amount) >= 0 ? '+' : ''}{fmt(Number(x.amount), 2)}
                </b>
                <span style={{ opacity: .55, fontSize: 11, minWidth: 74, textAlign: 'right' }}>
                  {fmt(Number(x.balance_after), 0)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* 질문 카드 — 여기만 흰 배경이다(목업 그대로) */}
        {r ? (
          <div className="ga-q-card">
            <div className="ga-q-head">
              <span className="ga-q-badge">ROUND {r.round_no}</span>
              <span className="ga-q-note">1분 후 결과가 공개됩니다</span>
              <span className="ga-q-timer">
                {betOpen ? `🕐 ${mmss(rLeft)}` : `⏳ ${mmss(jLeft)}`}
              </span>
            </div>
            <div className="ga-q-body">
              <div className="ga-q-icon">₿</div>
              <div>
                <div className="ga-q-t">비트코인, 1분 뒤<br />지금보다 오를까?</div>
                <div className="ga-q-p">
                  현재가 <b>${fmt(lobby?.price || 0, 2)}</b>
                  <span className="ga-q-up"> {r.open_price ? `${(((lobby?.price || 0) - Number(r.open_price)) / Number(r.open_price) * 100).toFixed(2)}%` : ''}</span>
                </div>
              </div>
            </div>

            {/* 걸어놓고 1분을 기다리는 동안 화면이 아무 말도 안 했다.
                지금 이기고 있는지 지고 있는지를 매 순간 말해준다. */}
            {(() => {
              const cur = Number(lobby?.price || 0);
              const base = Number(r.open_price || 0);
              if (!cur || !base) return null;
              const diff = cur - base;
              const rising = diff > 0, falling = diff < 0;
              const picked = me?.myChoice ? (UP(me.myChoice) ? 'up' : 'down') : null;
              const pct = ((diff / base) * 100).toFixed(3);
              const pctAbs = Math.abs(Number(pct)).toFixed(3);

              // 아직 안 골랐으면 방향만 알려준다
              if (!picked) {
                return (
                  <div style={{
                    marginTop: 10, padding: '10px 12px', borderRadius: 12,
                    background: 'rgba(255,255,255,.06)', fontSize: 13,
                  }}>
                    기준가 대비 지금{' '}
                    <b style={{ color: rising ? '#22C55E' : falling ? '#EF4444' : '#9CA3AF' }}>
                      {rising ? `▲ ${pctAbs}%` : falling ? `▼ ${pctAbs}%` : '변동 없음'}
                    </b>
                    {' · '}{betOpen ? '베팅 마감' : '결과까지'} <b>{mmss(tLeft)}</b>
                  </div>
                );
              }

              const flat = diff === 0;
              const winning = (picked === 'up' && rising) || (picked === 'down' && falling);
              const bg = flat ? 'rgba(156,163,175,.16)'
                : winning ? 'rgba(34,197,94,.16)' : 'rgba(239,68,68,.16)';
              const bd = flat ? '#9CA3AF' : winning ? '#22C55E' : '#EF4444';
              return (
                <div style={{
                  marginTop: 10, padding: '12px 14px', borderRadius: 12,
                  background: bg, border: `1px solid ${bd}55`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <b style={{ fontSize: 17, color: bd }}>
                      {flat ? '= 동률' : winning ? '🔥 이기고 있습니다' : '⚠️ 지고 있습니다'}
                    </b>
                    <span style={{ marginLeft: 'auto', fontSize: 13, opacity: .85 }}>
                      {betOpen ? `베팅 마감 ${mmss(rLeft)}` : `결과까지 ${mmss(jLeft)}`}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, opacity: .85, marginTop: 4 }}>
                    내 선택 <b>{picked === 'up' ? '⬆ 오른다' : '⬇ 내린다'}</b>
                    {me?.myBetPrice ? <> · <b>${fmt(Number(me.myBetPrice), 2)}</b> 에 걸었습니다</> : null}
                    {' · '}기준가 대비 {rising ? '▲' : falling ? '▼' : ''} {pctAbs}%
                  </div>
                  {flat ? (
                    <div style={{ fontSize: 12, opacity: .8, marginTop: 4 }}>
                      동률로 끝나면 <b>아무도 탈락하지 않습니다.</b>
                    </div>
                  ) : !winning ? (
                    <div style={{ fontSize: 12, opacity: .8, marginTop: 4 }}>
                      아직 끝나지 않았습니다 — 마감 전까지 뒤집힐 수 있습니다.
                    </div>
                  ) : null}

                  {/* 남은 시간이 줄어드는 게 눈에 보이게 */}
                  <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,.12)', marginTop: 10 }}>
                    <div style={{
                      width: `${Math.max(0, Math.min(100, tPct * 100))}%`,
                      height: '100%', borderRadius: 2, background: bd,
                      transition: 'width 1s linear',
                    }} />
                  </div>
                </div>
              );
            })()}

            {/* 마지막 10초. 숫자를 키우고 진동으로 알린다 — 여기서 손이 바빠져야 한다. */}
            {rLeft > 0 && rLeft <= 10 && (
              <div style={{
                marginTop: 10, padding: '10px 0', textAlign: 'center', borderRadius: 12,
                background: 'rgba(239,68,68,.14)', border: '1px solid rgba(239,68,68,.45)',
              }}>
                <div style={{ fontSize: 34, fontWeight: 900, lineHeight: 1, color: '#EF4444' }}>
                  {rLeft}
                </div>
                <div style={{ fontSize: 12, opacity: .85, marginTop: 4 }}>
                  {me?.myChoice ? '곧 결과가 나옵니다' : '아직 선택하지 않으셨습니다 — 무응답은 탈락입니다'}
                </div>
              </div>
            )}

            {/* 무엇을 보고 고를지 알 수 있게. 기준가 선과 내가 고른 지점을 같이 찍는다. */}
            <div style={{ margin: '10px 0 12px' }}>
              <BtcLiveChart
                openPrice={r.open_price ? Number(r.open_price) : null}
                fallbackPrice={Number(lobby?.price || 0) || null}
                mark={mark || serverMark}
              />
            </div>

            {/* 참여자들이 어디에 걸고 있는지 */}
            {(() => {
              const hu = Number(r.up_count || 0), hd = Number(r.down_count || 0);
              const bu = Number(royale?.botPicks?.botUp || 0);
              const bd = Number(royale?.botPicks?.botDown || 0);
              const uu = hu + bu, dd = hd + bd, tot = uu + dd;
              const up = tot ? Math.round((uu / tot) * 100) : 50;
              return (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', fontSize: 12, marginBottom: 6, fontWeight: 800 }}>
                    <span style={{ color: '#22C55E' }}>
                      ⬆ 오른다 {tot ? `${up}%` : '—'}
                      <span style={{ opacity: .65, fontWeight: 600 }}> {fmt(uu)}명</span>
                    </span>
                    <span style={{ marginLeft: 'auto', color: '#EF4444' }}>
                      <span style={{ opacity: .65, fontWeight: 600 }}>{fmt(dd)}명 </span>
                      {tot ? `${100 - up}%` : '—'} 내린다 ⬇
                    </span>
                  </div>
                  <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden',
                                background: 'rgba(0,0,0,.08)' }}>
                    <div style={{ width: `${tot ? up : 50}%`, background: '#22C55E',
                                  transition: 'width .8s cubic-bezier(.4,0,.2,1)' }} />
                    <div style={{ width: `${tot ? 100 - up : 50}%`, background: '#EF4444',
                                  transition: 'width .8s cubic-bezier(.4,0,.2,1)' }} />
                  </div>
                  <div style={{ fontSize: 11, opacity: .6, marginTop: 5, textAlign: 'center' }}>
                    {tot ? `${fmt(tot)}명이 선택했습니다` : '아직 아무도 고르지 않았습니다'}
                  </div>
                </div>
              );
            })()}


            {/* 얼마를, 몇 배로 */}
            {(() => {
              const w = Number(royale?.wallet?.balance || 0);
              const L0 = royale?.limits || { min: 100, max: 10000, leverages: [1, 2, 5] };
              // 라운드가 갈수록 판이 커진다 — 1R 100 · 2R 200 …
              const roundNo = Number(r.round_no || 1);
              const L = { ...L0, min: Math.max(Number(L0.min), roundNo * Number(L0.min)) };
              const locked = (joined && !alive) || !!me?.myChoice || busy || rLeft <= 0;
              // 1 단위로 조정한다. 100 단위로 묶어두면 원하는 금액을 못 맞춘다.
              const PRESETS = [100, 300, 500];
              const set = (v: number) => setBetAmt(v);
              return (
                <div style={{ marginBottom: 12, opacity: locked ? .55 : 1 }}>
                  {/* 금액은 정해진 세 가지만. 수기 입력은 받지 않는다(2026-10-16) */}
                  <div style={{ textAlign: 'center', marginBottom: 10 }}>
                    <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: -.5 }}>
                      {betAmt ? fmt(betAmt, 0) : '-'}
                    </div>
                    <div style={{ fontSize: 11, opacity: .6 }}>
                      wHANDY · 보유 {fmt(w, 0)}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    {PRESETS.map(v => {
                      const on = betAmt === v;
                      const poor = w < v;
                      return (
                        <button key={v} onClick={() => set(v)} disabled={locked || poor}
                          style={{ flex: 1, height: 48, borderRadius: 12, fontSize: 16, fontWeight: 800,
                                   border: on ? '2px solid #FF6B00' : '1px solid rgba(255,255,255,.18)',
                                   background: on ? 'rgba(255,107,0,.22)' : 'rgba(255,255,255,.08)',
                                   color: 'inherit', opacity: poor ? .35 : 1 }}>
                          {v.toLocaleString()}
                        </button>
                      );
                    })}
                  </div>

                  {/* 배수 — 맞혔을 때만 곱해진다 */}
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(L.leverages || [1, 2, 5]).map((v: number) => (
                      <button key={v} onClick={() => setLev(v)} disabled={locked}
                        style={{
                          flex: 1, height: 42, borderRadius: 12, fontSize: 15, fontWeight: 900,
                          border: lev === v ? '2px solid #FF6B00' : '1px solid rgba(255,255,255,.18)',
                          background: lev === v ? 'rgba(255,107,0,.18)' : 'transparent',
                          color: 'inherit',
                        }}>
                        {v}×
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, opacity: .65, marginTop: 6, textAlign: 'center' }}>
                    맞히면 <b style={{ color: '#22C55E' }}>+{fmt(betAmt * lev, 0)}</b> ·
                    틀리면 <b style={{ color: '#EF4444' }}>−{fmt(betAmt * lev, 0)}</b> 이고 탈락합니다
                  </div>
                </div>
              );
            })()}

            <div className="ga-q-bet">
              <button
                className={`ga-q-up-btn ${UP(me?.myChoice) ? 'picked' : ''}`}
                disabled={!alive && joined ? true : !!me?.myChoice || busy || rLeft <= 0 || betAmt <= 0}
                onClick={() => betRoyale('up', r.id)}
              >
                <div>⬆ 오른다</div><small>{fmt(betAmt, 0)} × {lev}배</small>
              </button>
              <button
                className={`ga-q-down-btn ${DOWN(me?.myChoice) ? 'picked' : ''}`}
                disabled={!alive && joined ? true : !!me?.myChoice || busy || rLeft <= 0 || betAmt <= 0}
                onClick={() => betRoyale('down', r.id)}
              >
                <div>⬇ 내린다</div><small>{fmt(betAmt, 0)} × {lev}배</small>
              </button>
            </div>
          </div>
        ) : (
          <div className="ga-card">
            {/* 기다리는 동안에도 시세는 보여야 한다. 판이 열릴 때 바로 판단할 수 있게. */}
            <BtcLiveChart
              openPrice={null}
              fallbackPrice={Number(lobby?.price || 0) || null}
              mark={null}
              height={140}
            />
            <div className="ga-center" style={{ marginTop: 12 }}>
            <img className="ga-wait-img" src={`${A}/char-orange-cheer.png?v=2`} alt="" />
            <div className="ga-card-t">곧 다음 판이 열립니다</div>
            <div className="ga-dim ga-sm">
              지금 <b>{fmt(humans.total)}명</b> 참가 중
              <br />놓쳐도 괜찮습니다 — <b>진행 중에도 입장</b>할 수 있어요.
            </div>
            </div>
          </div>
        )}


        {/* [2026-10-17] 참가 버튼을 시세 차트 바로 밑으로 올렸다.
            전에는 상금표·입장안내를 한참 지나야 나와서 들어갈 길이 안 보였다.
            문구도 '지금 참여하기' 로 통일한다. */}
        <button
          className="ga-btn-o ga-wide"
          disabled={joined || busy || !s || s.status === 'finished'}
          onClick={joinRoyale}
        >
          {!s ? '예정된 세션이 없습니다'
            : joined ? (alive ? '🎮 참가 완료 — 생존 중!' : `💀 R${me?.eliminated_at_round || '?'} 에서 탈락 — 다음 판을 기다리세요`)
            : '🎮 지금 참여하기 ›'}
        </button>

        {wheel && (
          <FreeWheel
            authHeaders={H}
            onClose={() => setWheel(false)}
            onDone={() => { loadRoyale(); load(); }}
          />
        )}

        {/* 나간 뒤 결과 */}
        {result && (
          <div
            onClick={() => setResult(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,.6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            }}>
            <div onClick={e => e.stopPropagation()} style={{
              width: '100%', maxWidth: 360, borderRadius: 18, overflow: 'hidden',
              background: '#16213E', border: '1px solid rgba(255,255,255,.12)',
            }}>
              <div style={{
                padding: '18px 20px 14px', textAlign: 'center',
                background: result.profit >= 0
                  ? 'linear-gradient(180deg, rgba(34,197,94,.22), transparent)'
                  : 'linear-gradient(180deg, rgba(239,68,68,.22), transparent)',
              }}>
                <div style={{ fontSize: 13, opacity: .75 }}>게임 결과</div>
                <div style={{
                  fontSize: 30, fontWeight: 900, letterSpacing: -.6, marginTop: 4,
                  color: result.profit >= 0 ? '#22C55E' : '#EF4444',
                }}>
                  {result.profit >= 0 ? '+' : ''}{fmt(result.profit, 0)}
                </div>
                <div style={{ fontSize: 12, opacity: .7 }}>wHANDY</div>
              </div>

              <div style={{ padding: '4px 20px 16px' }}>
                {[
                  ['투자금', `${fmt(result.bet, 0)} wHANDY`],
                  ['참여 횟수', `${fmt(result.count)}회`],
                  ['적중', `${fmt(result.wins)}회`],
                  ['수익률', result.bet > 0
                    ? `${result.profit >= 0 ? '+' : ''}${((result.profit / result.bet) * 100).toFixed(1)}%`
                    : '-'],
                  ['나간 라운드', `R${result.round}`],
                ].map(([k, v]) => (
                  <div key={k as string} style={{
                    display: 'flex', padding: '10px 0', fontSize: 14,
                    borderBottom: '1px solid rgba(255,255,255,.07)',
                  }}>
                    <span style={{ opacity: .7 }}>{k}</span>
                    <b style={{ marginLeft: 'auto' }}>{v}</b>
                  </div>
                ))}
                <button
                  onClick={() => setResult(null)}
                  className="ga-btn-o ga-wide"
                  style={{ marginTop: 16 }}>
                  확인
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 총 상금 */}
        <div className="ga-card ga-prize">
          <img src={`${A}/item-trophy.png?v=2`} alt="" />
          <div className="ga-prize-mid">
            <div className="ga-prize-l">오늘 누적 상금</div>
            <div className="ga-prize-v">{fmt(Number(royale?.pool?.prize || 0))}<small>wHANDY</small></div>
            <div style={{ fontSize: 11, opacity: .7, marginTop: 4, lineHeight: 1.55 }}>
              하루 동안 쌓은 상금을 <b>랭킹 TOP 10</b> 이 나눠 갖습니다
              <br />매일 자정에 자동 지급됩니다
            </div>
          </div>
          <div className="ga-pool-rank">
            <div className="ga-pool-rt">상금 규칙</div>
            <div className="ga-pool-rr">
              <span>한 판</span><b>{s?.total_rounds || 10}라운드</b>
            </div>
            <div className="ga-pool-rr">
              <span>받는 사람</span><b>랭킹 TOP 10</b>
            </div>
            <div className="ga-pool-rr">
              <span>지급</span><b>매일 자정</b>
            </div>
          </div>
        </div>

        {/* 등수별 상금 — 상단 카드를 내린 대신 여기에 둔다 (2026-10-17) */}
        {royale?.prizeSplit?.plan?.length > 0 && (
          <div className="ga-card" style={{ padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'baseline', marginBottom: 10 }}>
              <b style={{ fontSize: 15 }}>🏆 오늘 상금 TOP 10</b>
              <span style={{ fontSize: 12, opacity: .7 }}>매일 자정 지급</span>
            </div>
            {royale.prizeSplit.plan.map((row: any) => (
              <div key={row.rank} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
                borderTop: row.rank === 1 ? 0 : '1px solid rgba(128,128,128,.14)',
              }}>
                <span style={{
                  width: 24, height: 24, flexShrink: 0, borderRadius: 7,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800, color: '#fff',
                  background: row.rank === 1 ? '#F5A524'
                    : row.rank === 2 ? '#9AA3AE'
                    : row.rank === 3 ? '#C77B3C' : 'rgba(128,128,128,.5)',
                }}>{row.rank}</span>
                <span style={{ fontSize: 13, opacity: .7, width: 38 }}>{row.percent}%</span>
                <span style={{
                  flex: 1, minWidth: 0, fontSize: 13,
                  fontWeight: row.mine ? 800 : 600, letterSpacing: .3,
                  color: row.mine ? '#FF6B00' : undefined,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{row.name || '—'}{row.mine ? ' (나)' : ''}</span>
                <span style={{ fontWeight: 800, fontSize: 14 }}>
                  {fmt(row.amount || 0)}
                  <span style={{ fontSize: 10, fontWeight: 600, opacity: .6 }}> wHANDY</span>
                </span>
              </div>
            ))}
          </div>
        )}

        {canLate && (
          <div className="ga-late">
            <div className="ga-late-t">🔥 이미 R{s?.current_round} 진행 중입니다</div>
            <div className="ga-late-d">
              지금 들어가도 됩니다. <b>들어온 라운드는 탈락 판정에서 빼드립니다.</b>
              <br />상금은 하루 누적이라 <b>중간에 들어와도 불이익이 없습니다.</b>
            </div>
          </div>
        )}

        {/* 딴 돈을 들고 빠질 수 있어야 판단이 된다. 끝까지 강제하면 도박이 된다. */}
        {joined && alive && s?.status === 'live' && (
          <button
            className="ga-wide"
            disabled={busy}
            onClick={async () => {
              if (!confirm('게임에서 나가시겠습니까?')) return;
              setBusy(true);
              try {
                const d = await fetch('/api/game/royale/leave', { method: 'POST', headers: H() })
                  .then(r => r.json());
                if (!d.success) throw new Error(d.error || '나가지 못했습니다');
                setResult({
                  bet: Number(royale?.myBetTotal || 0),
                  count: Number(royale?.myBetCount || 0),
                  wins: Number(royale?.myWinCount || 0),
                  profit: Number(royale?.myProfit || 0),
                  round: Number(s?.current_round || 0),
                });
                loadRoyale();
              } catch (e: any) { toast.error(e.message); }
              finally { setBusy(false); }
            }}
            style={{
              marginBottom: 10, padding: 14, borderRadius: 12, fontSize: 15, fontWeight: 800,
              background: 'transparent', border: '1px solid rgba(255,255,255,.28)', color: 'inherit',
            }}
          >
            게임 나가기
          </button>
        )}


        {/* 실시간 수익 — 항상 보여준다. [2026-10-17]
            데이터가 없을 때 통째로 사라지니 화면이 들쭉날쭉했다.
            비어 있으면 '아직 없습니다' 를 띄우고 자리는 지킨다. */}
        {(
          <div className="ga-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px',
              background: 'linear-gradient(90deg, rgba(255,107,0,.20), transparent)',
            }}>
              <Crown size={18} style={{ color: '#FF6B00' }} />
              <b style={{ fontSize: 15 }}>실시간 수익</b>
              <span style={{ marginLeft: 'auto', fontSize: 11, opacity: .6 }}>오늘 · 상위 10명 보상</span>
            </div>

            {/* 내 수익을 맨 위에 고정한다. [2026-10-17]
                목록 아래쪽에 묻히거나 아예 안 보여서, 이겨도 반영이 안 되는
                것처럼 읽혔다. 플러스/마이너스를 색으로 바로 구분한다. */}
            {(() => {
              const pf = Number(royale?.todayProfit || 0);
              const bt = Number(royale?.todayBetTotal || 0);
              if (!bt) return null;
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 11, padding: '12px 14px',
                  background: 'rgba(255,107,0,.12)',
                  borderBottom: '1px solid rgba(255,255,255,.08)',
                }}>
                  <span style={{ width: 26, textAlign: 'center', fontSize: 11,
                                 fontWeight: 800, opacity: .8 }}>나</span>
                  <span style={{
                    width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: '#FF6B00', color: '#fff', fontWeight: 900, fontSize: 16,
                  }}>★</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 14 }}>내 오늘 수익</div>
                    <div style={{ fontSize: 11, opacity: .6 }}>
                      베팅 {fmt(bt, 0)} · {fmt(Number(royale?.todayBetCount ?? royale?.myBetCount ?? 0), 0)}회
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 16, fontWeight: 900,
                                  color: pf >= 0 ? '#22C55E' : '#EF4444' }}>
                      {pf >= 0 ? '+' : ''}{fmt(pf, 0)}
                    </div>
                    <div style={{ fontSize: 11, opacity: .55 }}>wHANDY</div>
                  </div>
                </div>
              );
            })()}

            <div style={{ maxHeight: 330, overflow: 'auto' }}>
              {(royale?.todayRanking || royale?.profits || []).length === 0 && (
                <div style={{ padding: '26px 16px', textAlign: 'center',
                              fontSize: 13, opacity: .55, lineHeight: 1.7 }}>
                  아직 오늘 수익이 없습니다.
                  <br />첫 주인공이 되어보세요!
                </div>
              )}
              {(royale?.todayRanking || royale?.profits || []).slice(0, 10).map((x: any, i: number) => {
                const pf = Number(x.profit);
                const medal = ['#F5C542', '#C9CCD1', '#CD8032'][i];
                return (
                  <div key={x.user_id} style={{
                    display: 'flex', alignItems: 'center', gap: 11, padding: '11px 14px',
                    borderBottom: '1px solid rgba(255,255,255,.06)',
                    background: x.mine ? 'rgba(255,107,0,.10)'
                      : i < 3 ? 'rgba(255,255,255,.035)' : 'transparent',
                  }}>
                    <span style={{
                      width: 26, textAlign: 'center', fontWeight: 900, fontSize: 14,
                      color: medal || 'rgba(255,255,255,.45)',
                    }}>{i + 1}</span>

                    <span style={{
                      width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: `hsl(${(Number(x.user_id) * 47) % 360} 65% 46%)`,
                      color: '#fff', fontWeight: 900, fontSize: 16,
                      border: medal ? `2px solid ${medal}` : '2px solid transparent',
                    }}>{String(x.nickname || '?').charAt(0).toUpperCase()}</span>

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 800, fontSize: 14, overflow: 'hidden',
                                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {x.nickname}{x.mine ? ' (나)' : ''}
                      </div>
                      <div style={{ fontSize: 11, opacity: .55 }}>
                        {i === 0 ? '오늘의 1위' : `${fmt(Number(x.plays || 0))}회 참여`}
                        {i < 10 ? ' · 보상 대상' : ''}
                      </div>
                    </div>

                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: 16, fontWeight: 900,
                        color: pf >= 0 ? '#22C55E' : '#EF4444',
                      }}>
                        {pf >= 0 ? '+' : ''}{fmt(pf, 0)}
                      </div>
                      <div style={{ fontSize: 10, opacity: .5 }}>wHANDY</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {(royale?.spinWins || []).length > 0 && (
              <div style={{ borderTop: '1px solid rgba(255,255,255,.08)' }}>
                <div style={{ padding: '10px 16px 6px', fontSize: 12, opacity: .6 }}>
                  🎰 룰렛 당첨 (2,000+)
                </div>
                {(royale.spinWins as any[]).map((x, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', fontSize: 13,
                  }}>
                    <span style={{
                      width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: '#F5C542', color: '#4A3000', fontWeight: 800, fontSize: 12,
                    }}>{String(x.nickname || '?').charAt(0).toUpperCase()}</span>
                    <span style={{ fontWeight: 600 }}>{x.nickname}</span>
                    <span style={{ fontSize: 11, opacity: .5 }}>무료 룰렛</span>
                    <b style={{ marginLeft: 'auto', color: '#F5C542' }}>🎉 +{fmt(Number(x.amount), 0)}</b>
                  </div>
                ))}
              </div>
            )}
            {(royale?.recentWins || []).length > 0 && (
              <div style={{ borderTop: '1px solid rgba(255,255,255,.08)' }}>
                <div style={{ padding: '10px 16px 6px', fontSize: 12, opacity: .6 }}>방금 받아간 사람</div>
                {(royale.recentWins as any[]).slice(0, 6).map(x => (
                  <div key={x.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px',
                    fontSize: 13,
                  }}>
                    <span style={{
                      width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: `hsl(${(Number(x.user_id) * 47) % 360} 65% 46%)`,
                      color: '#fff', fontWeight: 800, fontSize: 12,
                    }}>{String(x.nickname || '?').charAt(0).toUpperCase()}</span>
                    <span style={{ fontWeight: 600 }}>{x.nickname}</span>
                    <span style={{ fontSize: 11, opacity: .5 }}>
                      {x.kind === 'prize' ? '1등 상금' : `적중${x.leverage ? ` ${x.leverage}×` : ''}`}
                    </span>
                    <b style={{ marginLeft: 'auto', color: '#22C55E' }}>+{fmt(Number(x.amount), 0)}</b>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="ga-center ga-dim ga-sm">
          {joined && me?.joined_round > 0
            ? `R${me.joined_round} 입장 — 상금 대상은 아니지만 순위는 남습니다`
            : '당신의 예측이 랭킹을 만듭니다!'}
        </div>

        {/* [2026-10-17] 방 이름에 세션 id 를 쓰면 판이 바뀔 때마다 방이 새로
            생겨 사용자가 쓴 글이 전부 날아갔다. 로얄은 한 방으로 고정한다. */}
        <ChatBox authHeaders={H} room="royale:0" title="💬 로열 채팅" />

        {/* 하단 홍보 배너 — 가림 (2026-10-01). 자세히보기가 lobby 로 가는데 로비는 이제 없다 */}
        {false && (
        <div className="ga-promo">
          <img src={`${A}/banner-royale.png?v=2`} alt="" />
          <div>
            <div className="ga-promo-t">예측하는 자가 살아남는다!</div>
            <div className="ga-promo-d">매일 더 커지는 상금, 지금 도전하세요!</div>
          </div>
          <button className="ga-btn-o" onClick={() => setView('lobby')}>자세히 보기 ›</button>
        </div>
        )}
      </>
    );
  };

  // ══════════════════════════════════════════════ 랭킹 (ui02/ui11)
  const Rank = () => {
    // 랭킹은 10등까지. 상금 TOP 10 과 기준을 맞춘다 (2026-10-16)
    const list = (rank?.list || []).slice(0, 10);
    const medals = [`${A}/medal-1.png?v=2`, `${A}/medal-2.png?v=2`, `${A}/medal-3.png?v=2`];
    return (
      <>
        <Header title="게임으로 더 특별한 하루!" sub="플레이하고, 랭킹에 도전하고, 더 큰 보상을 받아보세요!" />

        {/* 내 랭크 카드 */}
        <div className="ga-myrank">
          <img className="ga-myrank-ch" src={`${A}/char-orange-coin.png?v=2`} alt="" />
          <div className="ga-myrank-mid">
            <div className="ga-dim ga-sm">내 랭킹</div>
            <div className="ga-myrank-l" style={{ color: leagueOf(prof?.league).color }}>
              {leagueOf(prof?.league).label} <img src={`${A}/item-shield.png?v=2`} alt="" />
            </div>
            <div className="ga-sm">전 세계 상위 <b>{prof?.topPercent ?? 100}%</b></div>
            <div className="ga-myrank-bar">
              <div style={{ width: `${Math.max(4, 100 - (prof?.topPercent ?? 100))}%` }} />
            </div>
          </div>
          <div className="ga-myrank-r">
            <div className="ga-lv">👑 LV.{prof?.level ?? 1}</div>
            <div className="ga-myrank-kpi">
              <div><div className="ga-sm ga-dim">📈 이번 주 승률</div><b>{prof?.winRate ?? 0}%</b></div>
              <div><div className="ga-sm ga-dim">💥 연속 적중</div><b>{prof?.streak ?? 0}회</b></div>
            </div>
          </div>
        </div>

        {/* 시즌 */}
        {season?.season && (
          <div className="ga-card ga-season-card">
            <div className="ga-season-head">
              <span>🏅 {season.season.name}</span>
              <span className="ga-dim ga-sm">종료까지 {dLeft(season.endsInSec)}</span>
            </div>
            {season.me && (
              <>
                <div className="ga-season-row">
                  <span className="ga-season-lg" style={{ color: leagueOf(season.me.league).color }}>
                    {season.me.league}
                  </span>
                  <span className="ga-sm">
                    {season.me.nextLeague
                      ? <>다음 <b>{season.me.nextLeague}</b> 까지 <b>{fmt(season.me.pointsToNext)}</b>점</>
                      : <>최고 리그입니다!</>}
                  </span>
                </div>
                <div className="ga-myrank-bar"><div style={{ width: `${season.me.progress}%` }} /></div>
                <div className="ga-season-rw">
                  <div><span className="ga-dim ga-sm">내 순위</span><b>{season.me.rank}위</b></div>
                  {/* [2026-10-17] 시즌 포인트·예상 보상은 실제 지급 근거가 없다.
                      game_rewards 에만 쌓이고 wHANDY 로 나간 적이 없어 가린다. */}
                  {false && (<>
                  <div><span className="ga-dim ga-sm">시즌 포인트</span><b>{fmt(season.me.seasonPoints)}</b></div>
                  <div><span className="ga-dim ga-sm">예상 보상</span>
                    <b className="ga-gold-t">{fmt(season.me.leagueReward + season.me.topBonus)}</b></div>
                  </>)}
                </div>
              </>
            )}
            {(season.unclaimed || []).map((u: any) => (
              <div key={u.season_id} className="ga-unclaimed">
                <span>🎁 {u.name} {u.league} {u.rank}위 보상 <b>{fmt(u.reward)} wHANDY</b></span>
                <button className="ga-btn-o ga-sm2" disabled={busy}
                  onClick={() => claimSeason(u.season_id)}>받기</button>
              </div>
            ))}
          </div>
        )}


        {/* 로얄 수익 TOP 제거 (2026-10-17)
            손실자(-1,000)가 'TOP' 목록에 올라가 있었다. 수익 순으로 줄을
            세우니 아무도 못 벌었으면 제일 덜 잃은 사람이 1등이 된다.
            아래 시즌 랭킹과 순위도 서로 달라 보여 혼란만 키웠다. */}

        {/* 탭 */}
        {/* 랭킹 범위 탭 — 가림 (2026-10-01) */}
        {false && (
        <div className="ga-tabs">
          {/* 친구·클랜은 가린다 — 전체 랭킹만 */}
          {([['all', '🌍 전체 랭킹']] as const).map(([k, l]) => (
            <button key={k} className={rankScope === k ? 'on' : ''} onClick={() => setRankScope(k as any)}>{l}</button>
          ))}
        </div>
        )}

        {/* 리그 랭킹 */}
        <div className="ga-card ga-league">
          {/* 핸디 리그 랭킹 — 가림 (2026-10-01) */}
          {false && (
          <div className="ga-league-h"><b>핸디 리그 랭킹</b>
            <span className="ga-dim ga-sm">{season?.season?.name || '이번 시즌'}</span></div>
          )}
          <div className="ga-lg-th"><span>순위</span><span>유저 정보</span><span>점수</span><span>변동</span></div>
          {/* 내 순위는 목록 위에 둔다. 2위인데 목록 맨 아래 붙어 있어서
              내가 몇 등인지 읽히지 않았다. (2026-10-17) */}
          {rank?.me && (
            <div className="ga-lg-r ga-lg-me">
              <span className="ga-lg-n">내 순위<br /><b>{rank.me.rank}</b></span>
              <span className="ga-lg-u">
                <img className="ga-av" src={`${A}/char-orange-point.png?v=2`} alt="" />
                <span><b>나</b><small>LV.{rank.me.level} · {leagueOf(rank.me.league).label}</small></span>
              </span>
              <span className="ga-lg-p">{fmt(rank.me.season_points)}</span>
              <span className="ga-lg-d">–</span>
            </div>
          )}

          {list.length === 0 && (
            <div className="ga-center ga-dim ga-sm" style={{ padding: 24, lineHeight: 1.6 }}>
              {rankScope === 'friend'
                ? <>추천으로 엮인 친구가 아직 없습니다.<br />내 추천코드를 공유해보세요!</>
                : rankScope === 'clan'
                ? <>클랜에 가입하면 클랜원 순위가 보입니다.</>
                : <>아직 랭킹이 없습니다</>}
            </div>
          )}
          {list.map((u: any, i: number) => (
            <div key={u.user_id} className="ga-lg-r">
              <span className="ga-lg-n">
                {i < 3 ? <img src={medals[i]} alt={`${i + 1}위`} /> : u.rank}
              </span>
              <span className="ga-lg-u">
                <img className="ga-av" src={`${A}/char-${i % 2 ? 'blue' : 'orange'}-wink.png?v=2`} alt="" />
                <span>
                  <b>{u.name}</b>
                  <small>LV.{u.level} · <i style={{ color: leagueOf(u.league).color }}>{leagueOf(u.league).label}</i></small>
                </span>
              </span>
              <span className="ga-lg-p">{fmt(u.season_points)}</span>
              <span className="ga-lg-d">{u.streak > 0 ? <em className="ga-up">▲ {u.streak}</em> : '–'}</span>
            </div>
          ))}
        </div>

        {/* 클랜 대전 · 시즌 보상 */}
        {/* 클랜 대전·시즌 보상 — 가림 (2026-10-01) */}
        {false && (
        <div className="ga-cards2">
          <div className="ga-card ga-clan">
            <div className="ga-card-t">🛡 클랜 대전 <span className="ga-dim ga-sm">준비 중</span></div>
            <div className="ga-clan-vs">
              <img src={`${A}/item-shield.png?v=2`} alt="" />
              <img className="ga-vs" src={`${A}/item-vs.png?v=2`} alt="VS" />
              <img className="ga-em-b" src={`${A}/item-shield.png?v=2`} alt="" />
            </div>
            <div className="ga-dim ga-sm ga-center">함께하는 클랜, 더 큰 승리!</div>
          </div>
          <div className="ga-card">
            <div className="ga-card-t">🎁 시즌 보상</div>
            <div className="ga-lgtable">
              {(season?.leagues || []).map((l: any) => (
                <div key={l.name} className={`ga-lgtable-r ${season?.me?.league === l.name ? 'on' : ''}`}>
                  <span style={{ color: leagueOf(l.name).color }}>{l.name}</span>
                  <em>{fmt(l.min)}점~</em>
                  <b>{fmt(l.reward)}</b>
                </div>
              ))}
            </div>
            <div className="ga-dim ga-sm ga-center">
              1·2·3위는 각각 50,000 / 30,000 / 10,000 추가
            </div>
          </div>
        </div>
        )}

        <div className="ga-promo">
          <img src={`${A}/char-highfive.png?v=2`} alt="" />
          <div>
            {/* 친구 초대 배너 — 가림 (2026-10-01) */}
            {false && (
            <div className="ga-badge-ev">EVENT</div>
            )}
            <div className="ga-promo-t">친구와 함께 리그에 도전하세요!</div>
            <div className="ga-promo-d">친구 초대하고, 추가 보상도 받아보세요!</div>
          </div>
        </div>
      </>
    );
  };

  // ══════════════════════════════════════════════ 미션 (ui06)
  // ══════════════════════════════════════════════ 룰렛 (별도 탭)
  const WheelTab = () => (
    // 탭 자체가 룰렛 화면이다. 버튼을 눌러 모달을 또 띄우면 한 단계가 헛돈다.
    <FreeWheel
      authHeaders={H}
      embed
      onClose={() => setView('royale' as any)}
      onDone={() => { loadRoyale(); load(); }}
    />
  );

  // ══════════════════════════════════════════════ 미션 (ui06)
  // ══════════════════════════════════════════════ 룰렛 (별도 탭)
  // ══════════════════════════════════════════════ 미션 (ui06)
  const Mission = () => {
    const st = streakInfo || {};
    const streak = Number(st.streak || 0);
    const doneSet = new Set<string>(st.days || []);
    const amount = Number(st.amount ?? 50);
    const need = Number(st.streakLen ?? 5);
    const boxes = Number(st.boxes || 0);

    // 이번 달 달력
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth();
    const first = new Date(y, m, 1).getDay();
    const last = new Date(y, m + 1, 0).getDate();
    const todayNum = now.getDate();
    const cells: (number | null)[] = [
      ...Array.from({ length: first }, () => null),
      ...Array.from({ length: last }, (_, i) => i + 1),
    ];
    const iso = (d: number) =>
      `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

    const sum = myLog?.summary || {};
    const CATS: [string, string][] = [
      ['게임 참여', '🎯'], ['게임 적중', '🏆'], ['상금', '👑'], ['룰렛', '🎡'], ['출첵', '📅'], ['랜덤박스', '🎁'],
    ];

    return (
      <>
        <Header title="매일 출석하고," sub={`하루 ${fmt(amount)} wHANDY 를 받아가세요`} />

        {/* 출석 달력 */}
        <div className="ga-card ga-cal">
          <div className="ga-cal-h">
            <b>{y}년 {m + 1}월</b>
            <span className="ga-cal-streak">🔥 연속 <b>{streak}</b>일</span>
          </div>
          <div className="ga-cal-wd">
            {['일', '월', '화', '수', '목', '금', '토'].map(d => <span key={d}>{d}</span>)}
          </div>
          <div className="ga-cal-grid">
            {cells.map((d, i) => {
              if (d === null) return <span key={i} />;
              const on = doneSet.has(iso(d));
              const today = d === todayNum;
              return (
                <span key={i} className={`ga-cal-d${on ? ' on' : ''}${today ? ' today' : ''}`}>
                  {on ? '✓' : d}
                </span>
              );
            })}
          </div>
          <button
            className="ga-btn-main"
            disabled={!st.claimable || busy}
            onClick={claimStreak}>
            {st.claimedToday ? '오늘 출석 완료' : `출석하고 ${fmt(amount)} 받기`}
          </button>
        </div>

        {/* 랜덤박스 */}
        <div className="ga-card ga-box">
          <div className="ga-box-h">
            <b>🎁 랜덤박스</b>
            <span className="ga-sm">{need}일 연속 출석마다 1개</span>
          </div>
          {boxes > 0 ? (
            st.hasReferral ? (
              <button className="ga-btn-box" disabled={busy} onClick={openBox}>
                상자 {boxes}개 열기 · {fmt((st.boxRange || [50, 300])[0])}~{fmt((st.boxRange || [50, 300])[1])} wHANDY
              </button>
            ) : (
              <div className="ga-box-lock">
                추천코드를 입력하면 열 수 있어요<br />
                <span className="ga-sm">보상 &gt; 추천코드 인증 · 나중에 입력해도 그대로 유효합니다</span>
              </div>
            )
          ) : (
            <div className="ga-box-wait">
              다음 상자까지 <b>{Number(st.toNextBox ?? need)}</b>일
              <div className="ga-box-bar">
                <i style={{ width: `${Math.min(100, ((streak % need) / need) * 100)}%` }} />
              </div>
            </div>
          )}
        </div>

        {/* 내 기록 */}
        <SecTitle>내 기록</SecTitle>
        <div className="ga-card">
          <div className="ga-sumgrid">
            {CATS.filter(([k]) => sum[k] !== undefined).map(([k, ic]) => (
              <div key={k} className="ga-sumcell">
                <span className="ga-sm">{ic} {k}</span>
                <b className={Number(sum[k]) < 0 ? 'ga-minus' : 'ga-plus'}>
                  {Number(sum[k]) > 0 ? '+' : ''}{fmt(sum[k])}
                </b>
              </div>
            ))}
            {!CATS.some(([k]) => sum[k] !== undefined) && (
              <div className="ga-dim" style={{ padding: 14 }}>아직 기록이 없어요</div>
            )}
          </div>
        </div>

        {/* 게임 참여 이력 */}
        <SecTitle>참여 이력</SecTitle>
        <div className="ga-card ga-histbox">
          {(myLog?.rows || []).length === 0 ? (
            <div className="ga-dim" style={{ padding: 14 }}>아직 기록이 없어요</div>
          ) : (myLog.rows as any[]).map(x => (
            <div key={x.id} className="ga-logrow">
              <span className="ga-sm">
                {String(x.createdAt).slice(5, 10).replace('-', '/')}{' '}
                {String(x.createdAt).slice(11, 16)} · {x.category}
              </span>
              <b className={Number(x.amount) < 0 ? 'ga-minus' : 'ga-plus'}>
                {Number(x.amount) > 0 ? '+' : ''}{fmt(x.amount)}
              </b>
            </div>
          ))}
        </div>

        {boxPrize !== null && (
          <div className="ga-boxpop" onClick={() => setBoxPrize(null)}>
            <div className="ga-boxpop-in">
              <div className="ga-boxpop-ic">🎁</div>
              <b>+{fmt(boxPrize)} wHANDY</b>
              <span className="ga-sm">축하합니다!</span>
            </div>
          </div>
        )}
      </>
    );
  };

  /**
   * [2026-10-22] 서버가 JSON 이 아닌 걸 돌려주면 그대로 터지면서
   * "Unexpected token '<'" 같은 말이 사용자에게 보였다.
   * 대개 앱이 옛 화면을 들고 있어 사라진 주소를 부르는 경우다 — 그렇게 안내한다.
   */
  async function asJson(r: Response) {
    const t = await r.text();
    try { return JSON.parse(t); }
    catch { throw new Error('앱을 완전히 껐다 켜주세요 (화면이 오래된 버전입니다)'); }
  }

  async function claimStreak() {
    if (busy || !streakInfo?.claimable) return;
    setBusy(true);
    try {
      const d = await fetch('/api/game/checkin/claim', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...H() }, body: '{}',
      }).then(asJson);
      if (!d.success) throw new Error(d.error || '받기에 실패했습니다');
      toast.success(`출석 보상 ${fmt(d.amount)} wHANDY 지급!`);
      loadStreak(); loadMyLog(); loadRoyale(); load();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  }

  async function openBox() {
    if (busy) return;
    setBusy(true);
    try {
      const d = await fetch('/api/game/checkin/box', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...H() }, body: '{}',
      }).then(asJson);
      if (!d.success) throw new Error(d.error || '상자를 열지 못했습니다');
      setBoxPrize(Number(d.amount));
      loadStreak(); loadMyLog(); loadRoyale(); load();
    } catch (e: any) { toast.error(e.message); } finally { setBusy(false); }
  }

  // ══════════════════════════════════════════════ HUMAN vs AI
  const HumanAi = () => {
    const s = hva?.session, r = hva?.round, ai = hva?.ai;
    const rLeft = secsTo(r?.locks_at);
    const locked = !!hva?.myChoice;
    const up = r?.up_count || 0, down = r?.down_count || 0;
    const tot = up + down || 1;

    return (
      <>
        <div className="ga-back"><button onClick={() => setView('lobby')}>‹ 아레나</button></div>

        <div className="ga-hva-hero">
          <img className="ga-hva-l" src={`${A}/char-orange-point.png?v=2`} alt="사람" />
          <img className="ga-hva-vs" src={`${A}/item-vs.png?v=2`} alt="VS" />
          <img className="ga-hva-r" src={`${A}/char-blue-thumb.png?v=2`} alt="AI" />
        </div>
        <div className="ga-center">
          <div className="ga-hva-t">Human <span>vs</span> AI</div>
          <div className="ga-dim ga-sm">인간의 직감 vs AI의 분석 — 승자는 누구?</div>
        </div>

        {!s ? (
          <div className="ga-card ga-center">
            <div className="ga-card-t">예정된 대결이 없습니다</div>
            <div className="ga-dim ga-sm">다음 시즌을 기다려 주세요!</div>
          </div>
        ) : (
          <>
            {/* 점수판 */}
            <div className="ga-score">
              <div className="ga-score-s">
                <div className="ga-score-l">👤 사람</div>
                <div className="ga-score-v">{s.human_score}</div>
              </div>
              <div className="ga-score-mid">
                <div className="ga-sm ga-dim">ROUND</div>
                <b>{s.current_round}/{s.total_rounds}</b>
              </div>
              <div className="ga-score-s ga-score-ai">
                <div className="ga-score-l">🤖 AI</div>
                <div className="ga-score-v">{s.ai_score}</div>
              </div>
            </div>

            {/* AI 분석 카드 — 라운드 중엔 방향을 가리고 근거만 보여준다 */}
            {ai && (
              <div className="ga-card ga-ai">
                <div className="ga-ai-head">
                  <span>🤖 AI 분석</span>
                  <span className="ga-ai-conf">확신도 {Math.round(Number(ai.confidence))}%</span>
                </div>
                <div className="ga-ai-bar"><div style={{ width: `${Number(ai.confidence)}%` }} /></div>
                <div className="ga-ai-reason">{ai.reason}</div>
                <div className={`ga-ai-pick ${ai.hidden ? 'hidden' : ''}`}>
                  {ai.hidden
                    ? '🔒 AI의 선택은 라운드가 끝나면 공개됩니다'
                    : <>AI의 선택: <b className={UP(ai.choice) ? 'ga-up' : 'ga-down'}>
                        {UP(ai.choice) ? '⬆ 상승' : '⬇ 하락'}</b></>}
                </div>
              </div>
            )}

            {/* 라운드 */}
            {r && r.status === 'open' ? (
              <div className="ga-q-card">
                <div className="ga-q-head">
                  <span className="ga-q-badge">ROUND {r.round_no}</span>
                  <span className="ga-q-note">당신의 직감을 보여주세요</span>
                  <span className="ga-q-timer">🕐 {mmss(rLeft)}</span>
                </div>
                <div className="ga-q-body">
                  <div className="ga-q-icon">₿</div>
                  <div>
                    <div className="ga-q-t">비트코인, 1분 뒤<br />지금보다 오를까?</div>
                    <div className="ga-q-p">기준가 <b>${fmt(Number(r.open_price), 2)}</b></div>
                  </div>
                </div>
                <div className="ga-q-bet">
                  <button className={`ga-q-up-btn ${UP(hva?.myChoice) ? 'picked' : ''}`}
                    disabled={locked || busy || rLeft <= 0}
                    onClick={() => predict('up', r.id)}>
                    <div>⬆ 오른다</div><small>사람 {Math.round(up / tot * 100)}%</small>
                  </button>
                  <button className={`ga-q-down-btn ${DOWN(hva?.myChoice) ? 'picked' : ''}`}
                    disabled={locked || busy || rLeft <= 0}
                    onClick={() => predict('down', r.id)}>
                    <div>⬇ 내린다</div><small>사람 {Math.round(down / tot * 100)}%</small>
                  </button>
                </div>
                <div className="ga-q-vote">
                  사람 쪽 답은 <b>다수결</b>입니다 · 지금 {fmt(up + down)}명 참여
                </div>
              </div>
            ) : (
              <div className="ga-card ga-center">
                <img className="ga-wait-img" src={`${A}/char-orange-cheer.png?v=2`} alt="" />
                <div className="ga-card-t">
                  {s.status === 'finished'
                    ? (s.human_score > s.ai_score ? '🎉 사람이 이겼습니다!'
                      : s.human_score < s.ai_score ? '🤖 AI가 이겼습니다' : '🤝 무승부')
                    : '다음 라운드를 준비 중입니다'}
                </div>
              </div>
            )}

            {/* 라운드별 기록 */}
            {!!hva?.history?.length && (
              <div className="ga-card">
                <div className="ga-card-t">📋 라운드 기록</div>
                <div className="ga-hist-th"><span>R</span><span>결과</span><span>사람</span><span>AI</span></div>
                {hva.history.map((h: any) => {
                  const hc = h.up_count === h.down_count ? null : (h.up_count > h.down_count ? 'UP' : 'DOWN');
                  const hw = hc === h.result;
                  return (
                    <div key={h.round_no} className="ga-hist-r">
                      <span>{h.round_no}</span>
                      <span className={UP(h.result) ? 'ga-up' : 'ga-down'}>
                        {UP(h.result) ? '⬆' : DOWN(h.result) ? '⬇' : '–'}
                      </span>
                      <span className={hw ? 'ga-win' : 'ga-lose'}>
                        {hc ? (UP(hc) ? '⬆' : '⬇') : '기권'}{hw ? ' ✓' : ''}
                      </span>
                      <span className={h.ai_correct ? 'ga-win' : 'ga-lose'}>
                        {UP(h.ai_choice) ? '⬆' : '⬇'}{h.ai_correct ? ' ✓' : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </>
    );
  };

  // ══════════════════════════════════════════════ 클랜 대전
  const Clan = () => {
    const w = clanWar?.war, mine = clanWar?.myClan;
    const a = Number(w?.score_a || 0), b = Number(w?.score_b || 0);
    const tot = a + b || 1;
    const aPct = Math.round(a / tot * 100);
    const left = secsTo(w?.ends_at);

    return (
      <>
        <div className="ga-back"><button onClick={() => setView('lobby')}>‹ 아레나</button></div>

        {w ? (
          <div className="ga-card ga-war">
            <div className="ga-war-head">
              <span>⚔ 클랜 대전</span>
              <span className="ga-dim ga-sm">종료까지 {hmsStr(left)}</span>
            </div>
            <div className="ga-war-vs">
              <div className={`ga-war-side ${clanWar?.mySide === String(w.clan_a) ? 'mine' : ''}`}>
                <img src={`${A}/${emblem(w.a_emblem)}.png?v=2`} alt="" />
                <div className="ga-war-name">{w.a_name}</div>
                <div className="ga-war-score">{fmt(a)}</div>
              </div>
              <img className="ga-war-vsimg" src={`${A}/item-vs.png?v=2`} alt="VS" />
              <div className={`ga-war-side ${clanWar?.mySide === String(w.clan_b) ? 'mine' : ''}`}>
                <img className="ga-em-b" src={`${A}/${emblem(w.b_emblem)}.png?v=2`} alt="" />
                <div className="ga-war-name">{w.b_name}</div>
                <div className="ga-war-score">{fmt(b)}</div>
              </div>
            </div>
            <div className="ga-war-bar"><div style={{ width: `${aPct}%` }} /></div>
            <div className="ga-dim ga-sm ga-center">점수 = 대전 기간에 소속 멤버가 맞힌 예측 수</div>

            {!!clanWar?.contributors?.length && (
              <div className="ga-contrib">
                <div className="ga-card-t">⭐ 우리 클랜 기여 TOP</div>
                {clanWar.contributors.map((c: any, i: number) => (
                  <div key={c.user_id} className="ga-contrib-r">
                    <span>{i + 1}</span><b>{c.name}</b><em>{c.hits}회 적중</em>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="ga-card ga-center">
            <img className="ga-wait-img" src={`${A}/item-vs.png?v=2`} alt="" />
            <div className="ga-card-t">진행 중인 대전이 없습니다</div>
            <div className="ga-dim ga-sm">클랜이 2개 이상 모이면 대전이 열립니다</div>
          </div>
        )}

        {/* 내 클랜 */}
        {mine ? (
          <div className="ga-card ga-myclan">
            <img src={`${A}/${emblem(mine.emblem)}.png?v=2`} alt="" />
            <div className="ga-myclan-mid">
              <div className="ga-myclan-n">[{mine.tag}] {mine.name}</div>
              <div className="ga-dim ga-sm">
                멤버 {mine.member_count}명 · 총점 {fmt(mine.total_points)} · {mine.role === 'leader' ? '클랜장' : '멤버'}
              </div>
            </div>
            <button className="ga-btn-ghost ga-sm2" disabled={busy}
              onClick={() => clanAction('/api/game/clans/leave')}>나가기</button>
          </div>
        ) : (
          <div className="ga-card">
            <div className="ga-card-t">클랜 만들기</div>
            <div className="ga-form">
              <input className="ga-in" placeholder="클랜 이름 (2~16자)" value={newClan.name}
                maxLength={16} onChange={e => setNewClan(v => ({ ...v, name: e.target.value }))} />
              <input className="ga-in ga-in-s" placeholder="태그" value={newClan.tag}
                maxLength={6} onChange={e => setNewClan(v => ({ ...v, tag: e.target.value.toUpperCase() }))} />
              <button className="ga-btn-o ga-sm2" disabled={busy || !newClan.name || !newClan.tag}
                onClick={() => clanAction('/api/game/clans', newClan)}>만들기</button>
            </div>
            <div className="ga-dim ga-sm">태그는 영문·숫자 2~6자입니다. 한 사람은 한 클랜만 가입할 수 있습니다.</div>
          </div>
        )}

        {/* 클랜 목록 */}
        <SecTitle>클랜 순위</SecTitle>
        <div className="ga-card ga-league">
          <div className="ga-cl-th"><span>#</span><span>클랜</span><span>멤버</span><span>총점</span><span /></div>
          {!clanList?.clans?.length && (
            <div className="ga-center ga-dim ga-sm" style={{ padding: 20 }}>아직 클랜이 없습니다</div>
          )}
          {(clanList?.clans || []).map((c: any, i: number) => (
            <div key={c.id} className="ga-cl-r">
              <span className="ga-lg-n">{i + 1}</span>
              <span className="ga-cl-u">
                <img src={`${A}/${emblem(c.emblem)}.png?v=2`} alt="" />
                <span><b>{c.name}</b><small>[{c.tag}] · {c.wins}승</small></span>
              </span>
              <span className="ga-cl-m">{c.member_count}</span>
              <span className="ga-lg-p">{fmt(c.total_points)}</span>
              <span>
                {!mine && (
                  <button className="ga-btn-o ga-sm2" disabled={busy}
                    onClick={() => clanAction(`/api/game/clans/${c.id}/join`)}>가입</button>
                )}
              </span>
            </div>
          ))}
        </div>
      </>
    );
  };

  // ══════════════════════════════════════════════ 보상 상점
  const Shop = () => (
    <>
      <div className="ga-back"><button onClick={() => setView('lobby')}>‹ 아레나</button></div>

      <div className="ga-card ga-coinbox">
        <img src={`${A}/item-coins.png?v=2`} alt="" />
        <div>
          <div className="ga-dim ga-sm">보유 코인</div>
          <div className="ga-coinbox-v">{fmt(shop?.coins ?? coins?.coins ?? 0)}</div>
        </div>
        <div className="ga-dim ga-sm ga-coinbox-n">
          적중하면 바로 쌓입니다<br />확신도가 높을수록 더 많이
        </div>
      </div>

      <SecTitle>wHANDY 교환</SecTitle>
      {/* [2026-10-17] 상점 목록을 들어냈다. 코인으로 'wHANDY 100' 을 사도
          game_rewards 에만 쌓이고 실제 지급이 없었다(shop 4건 400 전부 미지급). */}
      {false && (shop?.items || []).map((it: any) => (
        <div key={it.id} className="ga-shop">
          <img src={`${A}/${it.icon}.png?v=2`} alt="" />
          <div className="ga-shop-mid">
            <div className="ga-shop-t">{it.name}</div>
            <div className="ga-dim ga-sm">
              {fmt(it.cost_coins)} 코인
              {it.daily_limit > 0 && ` · 오늘 ${it.usedToday}/${it.daily_limit}`}
            </div>
          </div>
          <button className="ga-btn-o ga-sm2"
            disabled={busy || it.soldOut || (shop?.coins ?? 0) < it.cost_coins}
            onClick={() => buyItem(it.id, it.name)}>
            {it.soldOut ? '한도 소진' : (shop?.coins ?? 0) < it.cost_coins ? '코인 부족' : '교환'}
          </button>
        </div>
      ))}
      <div className="ga-dim ga-sm ga-center">
        교환한 wHANDY 는 지급 대기열로 들어가 순차 전송됩니다.
      </div>

      <SecTitle>코인 내역</SecTitle>
      <div className="ga-card">
        {!coins?.history?.length && (
          <div className="ga-center ga-dim ga-sm" style={{ padding: 16 }}>아직 내역이 없습니다</div>
        )}
        {(coins?.history || []).slice(0, 15).map((h: any, i: number) => (
          <div key={i} className="ga-coinrow">
            <span className="ga-dim ga-sm">
              {new Date(h.created_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="ga-coinrow-n">{h.note || h.reason}</span>
            <b className={Number(h.delta) >= 0 ? 'ga-up' : 'ga-down'}>
              {Number(h.delta) >= 0 ? '+' : ''}{fmt(Number(h.delta))}
            </b>
          </div>
        ))}
      </div>
    </>
  );

  // ─────────────────────────────── 렌더
  if (lobby && lobby.enabled === false) {
    return (
      <div className="ga ga-center" style={{ padding: 40 }}>
        <style>{CSS}</style>
        <img className="ga-wait-img" src={`${A}/char-blue-thumb.png?v=2`} alt="" />
        <div className="ga-card-t">게임은 곧 오픈합니다</div>
        <div className="ga-dim ga-sm">조금만 기다려 주세요!</div>
      </div>
    );
  }

  return (
    <div className="ga">
      <style>{CSS}</style>

      {/* 서브 내비 — 5개 뷰 */}
      <div className="ga-nav">
        {([
          // 아레나·보스·vs AI·클랜은 가린다 (2026-10-01)
          // 목록에서 빼면 버튼이 사라져 진입 자체가 막힌다.
          // ['buyback', '💰', '재매입'],   // 서브도메인 분리 여부 미정 — 임시 숨김 (2026-10-16)
          ['royale', '👑', '로얄'],
          ['rank', '🏆', '랭킹'],
          ['wheel', '🎡', '룰렛'],
          ['mission', '🎯', '미션'],
          // ['shop', '🛒', '상점'],   // [2026-10-17] 상품이 전부 미지급이라 목록을 뺐고,
          //                              남은 '보유 코인' 은 wHANDY 가 아닌 게임 포인트라
          //                              내 자산과 달라 혼란만 줬다. 탭째로 가린다.
        ] as const).map(([k, i, l]) => (
          <button key={k} className={view === k ? 'on' : ''} onClick={() => setView(k as any)}>
            <span>{i}</span>{l}
          </button>
        ))}
      </div>


      <div className={`ga-body${view === ('wheel' as any) ? ' ga-body-flush' : ''}`}>
        {!lobby && view === 'lobby' && (
          <div className="ga-center ga-dim" style={{ padding: 60 }}>불러오는 중…</div>
        )}
        {/* 컴포넌트 태그(<Lobby/>)로 쓰면 안 된다 — Lobby 는 매 렌더마다 새로 만들어지는
            함수라 React 가 '다른 컴포넌트'로 보고 매번 언마운트/재마운트한다.
            그러면 안에 있는 BossLog 의 state 가 3초마다 날아가고 계속 다시 부른다.
            그냥 호출해서 인라인하면 이 문제가 사라진다(뷰 함수들은 훅을 쓰지 않는다). */}
        {lobby && view === 'lobby' && Lobby()}
        {lobby && view === 'boss' && Boss()}
        {view === ('buyback' as any) && (
          <RoyalPage authHeaders={authHeaders} onBack={() => setView('royale' as any)} />
        )}
        {view === 'royale' && Royale()}
        {lobby && view === 'humanai' && HumanAi()}
        {lobby && view === 'clan' && Clan()}
        {view === 'rank' && Rank()}
        {view === ('wheel' as any) && WheelTab()}
        {view === 'mission' && Mission()}
        {view === 'shop' && Shop()}
      </div>
    </div>
  );
}

/** 실시간 레이드 로그 — 내 최근 예측 기록을 로그처럼 보여준다 */
function BossLog({ authHeaders, roundId }: { authHeaders: () => Record<string, string>; roundId?: number }) {
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => {
    fetch('/api/game/my-history', { headers: authHeaders() })
      .then(r => r.json()).then(d => { if (d.success) setRows((d.history || []).slice(0, 6)); })
      .catch(() => {});
  }, [authHeaders, roundId]);
  if (!rows.length) return <div className="ga-dim ga-sm">아직 기록이 없습니다. 첫 예측을 해보세요!</div>;
  return (
    <div className="ga-log">
      {rows.map(r => (
        <div key={r.id} className="ga-log-r">
          <span className="ga-log-t">
            {new Date(r.created_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
          </span>
          <span className={UP(r.choice) ? 'ga-up' : 'ga-down'}>
            {UP(r.choice) ? '⬆ 상승' : '⬇ 하락'}
          </span>
          <span className="ga-log-m">
            R{r.round_no} {r.correct === null ? '집계 중…'
              : r.correct ? `적중! +${r.damage} 데미지 🔥` : '아쉽네요…'}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * 실시간 채팅 — 3초 증분 폴링.
 * WS 를 안 쓴 이유는 서버 쪽 chatService 주석에 적어뒀다. 여기서는
 * "마지막으로 받은 id 이후"만 요청해서 보통 0~2줄만 받아온다.
 */
function ChatBox({ authHeaders, room, title }:
  { authHeaders: () => Record<string, string>; room: string; title: string }) {
  const [msgs, setMsgs] = useState<any[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [admin, setAdmin] = useState(false);   // 운영자면 글 삭제 버튼이 보인다
  const lastId = useRef(0);
  const box = useRef<HTMLDivElement | null>(null);

  const poll = useCallback(async () => {
    try {
      const d = await fetch(`/api/game/chat?room=${encodeURIComponent(room)}&since=${lastId.current}`,
        { headers: authHeaders() }).then(r => r.json());
      if (!d.success) return;
      if (d.admin !== undefined) setAdmin(Boolean(d.admin));
      if (!d.messages?.length) return;
      lastId.current = Number(d.messages[d.messages.length - 1].id);
      setMsgs(prev => [...prev, ...d.messages].slice(-60));
    } catch { /* 채팅은 실패해도 조용히 넘어간다 */ }
  }, [authHeaders, room]);

  /** 운영자 글 삭제 — 화면에서 바로 지운다 (2026-10-17) */
  async function removeMsg(id: number) {
    if (!confirm('이 글을 삭제할까요?')) return;
    try {
      const d = await fetch(`/api/game/chat/${id}`, { method: 'DELETE', headers: authHeaders() })
        .then(r => r.json());
      if (!d.success) throw new Error(d.error || '삭제하지 못했습니다');
      setMsgs(prev => prev.filter(m => Number(m.id) !== Number(id)));
      toast.success('삭제했습니다');
    } catch (e: any) { toast.error(e.message); }
  }

  useEffect(() => {
    lastId.current = 0; setMsgs([]);
    poll();
    const t = setInterval(poll, 3000);
    return () => clearInterval(t);
  }, [poll]);

  // 새 글이 오면 아래로 붙여준다
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [msgs.length]);

  async function send() {
    const m = text.trim();
    if (!m || busy) return;
    setBusy(true);
    try {
      const d = await fetch('/api/game/chat', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ room, message: m }),
      }).then(r => r.json());
      if (d.success) { setText(''); poll(); }
      else toast.error(d.error || '보내지 못했습니다');
    } catch { toast.error('네트워크 오류'); } finally { setBusy(false); }
  }

  return (
    <div className="ga-card">
      <div className="ga-card-t">{title}</div>
      <div className="ga-chat" ref={box}>
        {!msgs.length && <div className="ga-dim ga-sm ga-center" style={{ padding: 14 }}>
          첫 메시지를 남겨보세요!
        </div>}
        {msgs.map(m => (
          <div key={m.id} className="ga-chat-r">
            <span className="ga-chat-t">
              {new Date(m.created_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="ga-chat-n">{m.nickname}</span>
            <span className="ga-chat-m">{m.message}</span>
            {admin && (
              <button
                onClick={() => removeMsg(Number(m.id))}
                title="삭제"
                style={{
                  marginLeft: 6, flexShrink: 0, border: 0, background: 'transparent',
                  color: '#EF4444', fontSize: 15, lineHeight: 1, cursor: 'pointer',
                  padding: '0 4px', opacity: .8,
                }}>×</button>
            )}
          </div>
        ))}
      </div>
      <div className="ga-chat-in">
        <input className="ga-in" value={text} maxLength={200} placeholder="함께 응원해보세요! (200자)"
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') send(); }} />
        <button className="ga-btn-o ga-sm2" disabled={busy || !text.trim()} onClick={send}>보내기</button>
      </div>
      <div className="ga-quick-msg">
        {['모두 힘내요! 💪', '가즈아!! 🔥', '핸디 최고! 🧡'].map(q => (
          <button key={q} onClick={() => setText(q)}>{q}</button>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════ 스타일
const CSS = `
.ga{--o:#FF6B00;--o2:#FF9A2E;--bg:#0A1430;--bg2:#101E45;--cd:#16264F;--bd:rgba(120,160,255,.18);
 --tx:#fff;--dim:#8FA3D0;--up:#22C55E;--dn:#EF4444;--gold:#FFC53D;
 background:linear-gradient(180deg,#0D1B3E 0%,#0A1430 60%,#081027 100%);color:var(--tx);
 min-height:100%;font-family:inherit;padding-bottom:12px}
.ga *{box-sizing:border-box}
.ga-body{padding:0 12px 20px;display:flex;flex-direction:column;gap:14px}
.ga-dim{color:var(--dim)} .ga-sm{font-size:12px} .ga-center{text-align:center}
.ga-up{color:var(--up)} .ga-down{color:var(--dn)}

.ga-nav{position:sticky;top:0;z-index:5;display:flex;gap:6px;padding:10px 12px;
 background:rgba(10,20,48,.92);backdrop-filter:blur(8px);border-bottom:1px solid var(--bd)}
.ga-nav button{flex:1;display:flex;flex-direction:column;align-items:center;gap:2px;
 padding:7px 0;border:1px solid var(--bd);border-radius:12px;background:var(--cd);
 color:var(--dim);font-size:11px;font-weight:700;cursor:pointer}
.ga-nav button span{font-size:16px;line-height:1}
.ga-nav button.on{background:linear-gradient(135deg,var(--o),var(--o2));color:#fff;border-color:transparent}

.ga-head{display:flex;align-items:center;justify-content:space-between;padding:14px 2px 0}
.ga-head-t{font-size:19px;font-weight:800;line-height:1.25}
.ga-head-s{font-size:12px;color:var(--dim);margin-top:3px}
.ga-head-me{display:flex;flex-direction:column;align-items:center;gap:2px;font-size:11px;font-weight:700}
.ga-head-me img{width:40px;height:40px;border-radius:50%;object-fit:cover;border:2px solid var(--o)}

.ga-back button{background:none;border:0;color:var(--dim);font-size:13px;font-weight:700;
 padding:12px 0 0;cursor:pointer}

.ga-sec{display:flex;align-items:center;justify-content:space-between;margin-top:4px}
.ga-sec h3{margin:0;font-size:16px;font-weight:800}
.ga-more{background:none;border:0;color:var(--dim);font-size:12px;cursor:pointer}

.ga-btn-o{background:linear-gradient(135deg,var(--o),var(--o2));color:#fff;border:0;
 border-radius:999px;padding:9px 16px;font-size:13px;font-weight:800;cursor:pointer;
 box-shadow:0 4px 14px rgba(255,107,0,.35)}
.ga-btn-o:disabled{opacity:.5;box-shadow:none;cursor:default}
.ga-btn-b{background:linear-gradient(135deg,#2563EB,#3B82F6);color:#fff;border:0;
 border-radius:999px;padding:9px 16px;font-size:13px;font-weight:800;cursor:pointer}
.ga-btn-ghost{background:rgba(255,255,255,.08);color:#fff;border:1px solid var(--bd);
 border-radius:999px;padding:7px 13px;font-size:12px;font-weight:700;cursor:pointer}
.ga-wide{width:100%;padding:15px;font-size:15px;border-radius:16px}
.ga-sm2{padding:6px 12px;font-size:12px}

.ga-card{background:var(--cd);border:1px solid var(--bd);border-radius:16px;padding:14px}
.ga-card-t{font-size:14px;font-weight:800;margin-bottom:8px}

/* 히어로 */
.ga-hero{position:relative;border-radius:18px;overflow:hidden;border:1px solid var(--bd);cursor:pointer}
.ga-hero img{width:100%;display:block;aspect-ratio:1200/295;object-fit:cover}
.ga-hero-foot{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 12px;
 background:linear-gradient(180deg,rgba(10,20,48,0),rgba(10,20,48,.95) 40%)}
.ga-live{font-size:11px;font-weight:800;color:#fff;background:rgba(0,0,0,.45);
 border-radius:999px;padding:6px 10px;white-space:nowrap}
.ga-live b{color:var(--gold)}

/* 게임 카드 3장 */
.ga-cards3{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
.ga-cards2{display:grid;grid-template-columns:repeat(2,1fr);gap:8px}
.ga-gc{position:relative;border-radius:16px;overflow:hidden;border:1px solid var(--bd);
 min-height:210px;padding:9px;display:flex;flex-direction:column;gap:6px;cursor:pointer}
/* 카드마다 색을 달리해 구분한다(그림 한 장에 의존하지 않는다) */
.ga-gc-boss{background:linear-gradient(180deg,#3B0F0F,#140A16)}
.ga-gc-royale{background:linear-gradient(180deg,#10265E,#0B1330)}
.ga-gc-pick{background:linear-gradient(180deg,#2E1257,#140A2E)}
.ga-gc-art{position:absolute;left:50%;top:38%;transform:translate(-50%,-50%);
 width:118%;max-width:none;z-index:0;opacity:.95}
.ga-gc-art-s{width:62%;top:32%}
.ga-gc::after{content:'';position:absolute;inset:0;z-index:1;
 background:linear-gradient(180deg,rgba(6,12,30,0) 30%,rgba(6,12,30,.86) 62%,rgba(6,12,30,.97) 100%)}
.ga-gc>*:not(.ga-gc-art){position:relative;z-index:2}
.ga-gc-top{display:flex;gap:4px;flex-wrap:wrap}
.ga-gc-body{margin-top:auto;display:flex;flex-direction:column;gap:4px}
.ga-gc-t{font-size:14px;font-weight:900;line-height:1.18}
.ga-gc-d{font-size:10px;color:#D6E0F5;line-height:1.35}
.ga-gc-cta{width:100%;padding:8px 0;font-size:11px;margin-top:2px}
.ga-chip-live{background:#EF4444;color:#fff;font-size:9px;font-weight:800;border-radius:999px;padding:3px 7px}
.ga-chip-n{background:rgba(0,0,0,.5);color:#fff;font-size:9px;font-weight:700;border-radius:999px;padding:3px 7px}

.ga-streak{background:rgba(255,255,255,.93);border-radius:10px;padding:5px 6px}
.ga-streak-t{font-size:9px;font-weight:800;color:#1B2A4A;margin-bottom:3px}
.ga-streak-d{display:flex;gap:2px}
.ga-streak-d span{flex:1;aspect-ratio:1;border-radius:50%;background:#DDE4F0;color:#fff;
 font-size:8px;display:flex;align-items:center;justify-content:center;font-weight:900}
.ga-streak-d span.on{background:linear-gradient(135deg,var(--o),var(--o2))}

/* 카운트다운 */
.ga-cd{display:flex;align-items:flex-start;justify-content:center;gap:2px}
.ga-cd-g{position:relative;display:flex;flex-direction:column;align-items:center}
.ga-cd-b{background:linear-gradient(180deg,#C2410C,#9A3412);border-radius:8px;
 padding:6px 9px;font-size:18px;font-weight:900;font-variant-numeric:tabular-nums;min-width:40px;text-align:center}
.ga-cd-l{font-size:9px;color:var(--dim);margin-top:2px}
.ga-cd-sep{position:absolute;right:-5px;top:5px;font-size:14px;font-weight:900;color:var(--dim)}
.ga-cd-dark .ga-cd-b{background:rgba(0,0,0,.62);border:1px solid rgba(255,255,255,.18);
 font-size:13px;padding:4px 5px;min-width:26px}
.ga-cd-dark .ga-cd-l{font-size:7px}
.ga-cd-dark .ga-cd-sep{right:-3px;top:2px;font-size:10px}

/* 곧 시작 */
/* 가로배너 카드 — 그림을 있는 그대로 크게 보여주고 배지·버튼만 얹는다 */
.ga-wide2{display:flex;flex-direction:column;gap:10px}
.ga-soon{position:relative;border-radius:16px;overflow:hidden;border:1px solid var(--bd);
 cursor:pointer;line-height:0}
.ga-soon img{width:100%;display:block}
.ga-soon-badge{position:absolute;top:8px;right:8px;z-index:2;background:rgba(0,0,0,.6);
 border:1px solid rgba(255,255,255,.2);color:#fff;font-size:10px;font-weight:800;
 border-radius:999px;padding:4px 9px;line-height:1.2}
.ga-soon-cta{position:absolute;bottom:8px;right:8px;z-index:2;padding:7px 13px;font-size:12px}

/* 내 기록 */
.ga-stats{display:flex;gap:6px;background:var(--cd);border:1px solid var(--bd);
 border-radius:16px;padding:10px}
.ga-stats-badge{display:flex;flex-direction:column;align-items:center;gap:2px;
 border:2px solid;border-radius:12px;padding:6px 8px;min-width:58px}
.ga-stats-badge img{width:26px}
.ga-stats-badge span{font-size:9px;font-weight:900}
.ga-stat{flex:1;text-align:center;border-left:1px solid var(--bd);padding-left:4px}
.ga-stat-l{font-size:9px;color:var(--dim)}
.ga-stat-v{font-size:13px;font-weight:800;margin-top:3px;white-space:nowrap}

/* 보상 풀 */
.ga-pool{display:flex;align-items:center;gap:8px;background:linear-gradient(135deg,#2A1B08,#16264F);
 border:1px solid rgba(255,197,61,.35);border-radius:16px;padding:12px}
.ga-pool-img{width:62px}
.ga-pool-mid{flex:1;min-width:0}
.ga-pool-l{font-size:10px;color:var(--dim)}
.ga-pool-v{font-size:19px;font-weight:900;color:var(--gold);line-height:1.15;word-break:break-all}
.ga-pool-v small{font-size:10px;margin-left:3px;color:#fff}
.ga-pool-rank{background:rgba(0,0,0,.35);border-radius:10px;padding:12px 14px;min-width:112px;min-width:150px}
.ga-pool-rt{font-size:13px;color:var(--gold);font-weight:800;margin-bottom:3px}
.ga-pool-rr{display:flex;justify-content:space-between;gap:6px;font-size:13px;color:var(--dim);padding:3px 0}
.ga-pool-rr b{color:#fff}

/* 빠른 메뉴 */
.ga-quick{display:grid;grid-template-columns:repeat(4,1fr);gap:7px}
.ga-q{background:#fff;border:0;border-radius:14px;padding:11px 4px;display:flex;
 flex-direction:column;align-items:center;gap:2px;cursor:pointer}
.ga-q-i{font-size:20px}
.ga-q-t{font-size:11px;font-weight:800;color:#1B2A4A}
.ga-q-d{font-size:8px;color:#7A88A6;text-align:center;line-height:1.2}

/* ───── 보스 */
.ga-boss-hero{position:relative;border-radius:18px;overflow:hidden;border:1px solid var(--bd)}
.ga-boss-hero img{width:100%;display:block;aspect-ratio:16/11;object-fit:cover}
.ga-boss-live{position:absolute;bottom:10px;left:12px;display:flex;gap:5px}

.ga-hp-head{display:flex;justify-content:space-between;font-size:13px;font-weight:800;margin-bottom:7px}
.ga-hp-bar{position:relative;height:26px;border-radius:999px;background:#2A1520;overflow:hidden;
 border:1px solid rgba(255,255,255,.12)}
.ga-hp-fill{height:100%;background:linear-gradient(90deg,#DC2626,#F59E0B,#FBBF24);
 transition:width .6s ease}
.ga-hp-txt{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;
 font-size:11px;font-weight:800;text-shadow:0 1px 3px #000;font-variant-numeric:tabular-nums}
.ga-hp-pct{position:absolute;right:9px;top:0;bottom:0;display:flex;align-items:center;
 font-size:11px;font-weight:900;text-shadow:0 1px 3px #000}

.ga-row3{display:grid;grid-template-columns:1fr 1.1fr 1fr;gap:7px}
.ga-mini{padding:10px 8px;text-align:center}
.ga-mini-l{font-size:9px;color:var(--dim)}
.ga-mini-v{font-size:14px;font-weight:900;margin-top:3px;font-variant-numeric:tabular-nums}
.ga-combo{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px}
.ga-combo-l{font-size:8px;color:var(--dim);text-align:center;line-height:1.2}
.ga-combo-v{font-size:17px;font-weight:900;font-style:italic;
 background:linear-gradient(180deg,#FFE9A8,#F97316);-webkit-background-clip:text;
 background-clip:text;color:transparent}

.ga-bet{display:grid;grid-template-columns:1fr 1fr;gap:9px}
.ga-bet button{border-radius:16px;padding:16px 8px;border:2px solid;cursor:pointer;
 display:flex;flex-direction:column;align-items:center;gap:3px;color:#fff}
.ga-bet-up{background:linear-gradient(160deg,#064E3B,#065F46);border-color:#34D399;
 box-shadow:0 0 22px rgba(52,211,153,.22) inset}
.ga-bet-down{background:linear-gradient(160deg,#4C0519,#7F1D1D);border-color:#F87171;
 box-shadow:0 0 22px rgba(248,113,113,.22) inset}
.ga-bet button:disabled{opacity:.45;cursor:default}
.ga-bet button.picked{outline:3px solid var(--gold);opacity:1}
.ga-bet-cap{font-size:10px;color:#CBD5E1}
.ga-bet-big{font-size:21px;font-weight:900}
.ga-bet-sub{font-size:9px;color:#CBD5E1}
.ga-round-note{text-align:center;font-size:11px;color:var(--dim);margin-top:-4px}
.ga-round-note b{color:#fff}

.ga-team{display:flex;justify-content:space-between;font-size:11px;margin-bottom:6px}
.ga-team div{display:flex;flex-direction:column;gap:2px}
.ga-team .ga-r{align-items:flex-end}
.ga-team b{font-size:14px;font-weight:900}
.ga-team small{font-size:10px;color:var(--dim)}
.ga-team-bar{height:9px;border-radius:999px;background:#EF4444;overflow:hidden}
.ga-team-bar div{height:100%;background:linear-gradient(90deg,#10B981,#34D399);transition:width .6s}

.ga-stage{position:relative;display:grid;grid-template-columns:repeat(5,1fr);gap:2px;padding-top:6px}
.ga-stage-line{position:absolute;left:8%;right:8%;top:34px;height:4px;border-radius:999px;
 background:rgba(255,255,255,.14)}
.ga-stage-line div{height:100%;border-radius:999px;background:linear-gradient(90deg,#34D399,#F59E0B);transition:width .6s}
.ga-stage-i{text-align:center;opacity:.42}
.ga-stage-i.done,.ga-stage-i.now{opacity:1}
.ga-stage-i img{width:40px;height:40px;object-fit:contain}
.ga-stage-p{font-size:11px;font-weight:900;margin-top:2px}
.ga-stage-s{font-size:8px;color:var(--dim)}
.ga-stage-i.done .ga-stage-s{color:var(--up)}
.ga-stage-i.now .ga-stage-s{color:var(--o2)}

.ga-reward{display:flex;align-items:center;gap:10px}
.ga-reward img{width:52px}
.ga-reward-v{font-size:16px;font-weight:900;color:var(--gold)}

.ga-log{display:flex;flex-direction:column;gap:6px}
.ga-log-r{display:flex;gap:7px;align-items:center;font-size:11px}
.ga-log-t{color:var(--dim);font-variant-numeric:tabular-nums}
.ga-log-m{color:#D6E0F5}

/* ───── 로열 */
.ga-roy-hero{position:relative;border-radius:18px;overflow:hidden;border:1px solid var(--bd);
 text-align:center;padding-bottom:12px;background:linear-gradient(180deg,#132A5E,#0A1430)}
.ga-roy-hero img{width:100%;display:block;aspect-ratio:449/312;object-fit:cover}
.ga-roy-t{font-size:20px;font-weight:900;margin-top:8px}
.ga-roy-t b{color:var(--o2)}
.ga-roy-s{font-size:11px;color:var(--dim);margin-top:3px}
.ga-roy-cd{text-align:center}
.ga-roy-cd-l{font-size:12px;color:var(--dim);margin-bottom:7px}
.ga-roy-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:7px}
.ga-roy-stat{background:var(--cd);border:1px solid var(--bd);border-radius:14px;padding:11px 6px;text-align:center}
.ga-roy-stat-l{font-size:10px;color:var(--dim)}
.ga-roy-stat-v{font-size:17px;font-weight:900;margin-top:3px}

.ga-q-card{background:#fff;border-radius:18px;padding:13px;color:#152040}
.ga-q-head{display:flex;align-items:center;gap:7px}
.ga-q-badge{background:linear-gradient(135deg,var(--o),var(--o2));color:#fff;font-size:10px;
 font-weight:900;border-radius:999px;padding:5px 11px}
.ga-q-note{font-size:10px;color:#7A88A6;flex:1}
.ga-q-timer{font-size:11px;font-weight:800;color:var(--o);background:#FFF1E4;
 border-radius:999px;padding:4px 9px;font-variant-numeric:tabular-nums}
.ga-q-body{display:flex;gap:10px;align-items:center;margin:12px 0}
.ga-q-icon{width:52px;height:52px;border-radius:50%;background:#F7931A;color:#fff;
 font-size:30px;font-weight:900;display:flex;align-items:center;justify-content:center;flex:0 0 auto}
.ga-q-t{font-size:17px;font-weight:900;line-height:1.3}
.ga-q-p{font-size:11px;color:#7A88A6;margin-top:3px}
.ga-q-p b{color:#152040}
.ga-q-up{color:var(--up);font-weight:800}
.ga-q-bet{display:grid;grid-template-columns:1fr 1fr;gap:9px}
.ga-q-bet button{border:0;border-radius:14px;padding:14px 6px;color:#fff;cursor:pointer;
 display:flex;flex-direction:column;align-items:center;gap:2px}
.ga-q-bet button div{font-size:17px;font-weight:900}
.ga-q-bet button small{font-size:9px;opacity:.9}
.ga-q-bet button:disabled{opacity:.45;cursor:default}
.ga-q-bet button.picked{outline:3px solid var(--gold);opacity:1}
.ga-q-up-btn{background:linear-gradient(135deg,var(--o),var(--o2))}
.ga-q-down-btn{background:linear-gradient(135deg,#2563EB,#3B82F6)}

.ga-prize{display:flex;align-items:center;gap:9px;
 background:linear-gradient(135deg,#2A1B08,#16264F);border-color:rgba(255,197,61,.35)}
.ga-prize img{width:64px}
.ga-prize-mid{flex:1;min-width:0}
.ga-prize-l{font-size:11px;color:var(--gold);font-weight:800}
.ga-prize-v{font-size:20px;font-weight:900;line-height:1.15;word-break:break-all}
.ga-prize-v small{font-size:10px;margin-left:3px;color:var(--dim)}
.ga-wait-img{width:90px;margin:0 auto 8px;display:block}

.ga-promo{display:flex;align-items:center;gap:9px;background:linear-gradient(135deg,#17265A,#1E1147);
 border:1px solid var(--bd);border-radius:16px;padding:10px}
.ga-promo img{width:62px;border-radius:10px;object-fit:cover;flex:0 0 auto}
.ga-promo>div{flex:1;min-width:0}
.ga-promo-t{font-size:13px;font-weight:800}
.ga-promo-d{font-size:10px;color:var(--dim);margin-top:2px}
.ga-badge-ev{display:inline-block;background:#2563EB;color:#fff;font-size:9px;font-weight:900;
 border-radius:6px;padding:2px 7px;margin-bottom:3px}

/* ───── 랭킹 */
.ga-myrank{position:relative;display:flex;gap:8px;align-items:center;padding:12px;
 background:linear-gradient(135deg,#3A1F06,#16264F);border:1px solid rgba(255,197,61,.4);border-radius:18px}
.ga-myrank-ch{width:70px;flex:0 0 auto}
.ga-myrank-mid{flex:1;min-width:0}
.ga-myrank-l{font-size:22px;font-weight:900;display:flex;align-items:center;gap:5px;line-height:1.1}
.ga-myrank-l img{width:26px}
.ga-myrank-bar{height:7px;border-radius:999px;background:rgba(255,255,255,.14);margin-top:5px;overflow:hidden}
.ga-myrank-bar div{height:100%;background:linear-gradient(90deg,var(--o),var(--gold))}
.ga-myrank-r{display:flex;flex-direction:column;gap:5px;align-items:flex-end}
.ga-lv{background:#000;border:1px solid var(--gold);border-radius:999px;padding:4px 10px;
 font-size:11px;font-weight:900;color:var(--gold)}
.ga-myrank-kpi{display:flex;gap:5px}
.ga-myrank-kpi>div{background:rgba(0,0,0,.4);border-radius:10px;padding:5px 7px;text-align:center}
.ga-myrank-kpi b{font-size:14px;font-weight:900}

.ga-tabs{display:flex;gap:7px}
.ga-tabs button{flex:1;padding:11px 0;border-radius:12px;border:1px solid var(--bd);
 background:var(--cd);color:var(--dim);font-size:13px;font-weight:800;cursor:pointer}
.ga-tabs button.on{background:linear-gradient(135deg,var(--o),var(--o2));color:#fff;border-color:transparent}

.ga-league{padding:12px 10px}
.ga-league-h{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;font-size:14px}
.ga-lg-th,.ga-lg-r{display:grid;grid-template-columns:40px 1fr 62px 44px;align-items:center;gap:4px}
.ga-lg-th{font-size:10px;color:var(--dim);padding:0 0 7px;border-bottom:1px solid var(--bd)}
.ga-lg-th span:nth-child(3),.ga-lg-th span:nth-child(4){text-align:right}
.ga-lg-r{padding:8px 0;border-bottom:1px solid rgba(120,160,255,.09);font-size:12px}
.ga-lg-n{text-align:center;font-weight:900;font-size:13px}
.ga-lg-n img{width:28px;display:block;margin:0 auto}
.ga-lg-u{display:flex;align-items:center;gap:7px;min-width:0}
.ga-av{width:30px;height:30px;border-radius:50%;object-fit:cover;background:#1E2C52;flex:0 0 auto}
.ga-lg-u span{display:flex;flex-direction:column;min-width:0}
.ga-lg-u b{font-size:12px;font-weight:800;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ga-lg-u small{font-size:9px;color:var(--dim)}
.ga-lg-u i{font-style:normal;font-weight:800}
.ga-lg-p{text-align:right;font-weight:900;font-variant-numeric:tabular-nums}
.ga-lg-d{text-align:right;font-size:11px;color:var(--dim)}
.ga-lg-d em{font-style:normal;font-weight:800}
.ga-lg-me{background:rgba(255,107,0,.12);border:1px solid rgba(255,107,0,.4);
 border-radius:12px;padding:8px 6px;margin-top:6px}
.ga-lg-me .ga-lg-n{font-size:9px;color:var(--o2);line-height:1.2}
.ga-lg-me .ga-lg-n b{font-size:15px;color:#fff}

.ga-clan-vs{display:flex;align-items:center;justify-content:center;gap:8px;padding:6px 0}
.ga-clan-vs img{width:44px}
.ga-clan-vs .ga-vs{width:52px}
.ga-season{display:flex;gap:5px;justify-content:center;padding:4px 0 8px}
.ga-season-i{flex:1;text-align:center;opacity:.45;border-radius:10px;padding:5px 2px}
.ga-season-i.on{opacity:1;background:rgba(255,197,61,.14);border:1px solid rgba(255,197,61,.45)}
.ga-season-i img{width:40px;height:40px;object-fit:contain}
.ga-season-i span{display:block;font-size:9px;font-weight:700;margin-top:2px}

/* 룰렛 탭은 배경 그림이 화면을 꽉 채운다 — 본문 여백을 없앤다 */
.ga-body-flush{padding:0 !important;gap:0 !important;overflow:hidden}

/* ───── 룰렛 탭 · 출석 달력 · 랜덤박스 (2026-10-22) */
.ga-wheel-tab{text-align:center;padding:22px 16px}
.ga-wheel-emoji{font-size:54px;line-height:1;margin-bottom:10px}
.ga-wheel-left{font-size:14px;opacity:.85;margin-bottom:14px}
.ga-wheel-left b{font-size:20px;color:var(--o)}
.ga-wheel-note{font-size:11px;opacity:.5;margin-top:10px}
.ga-btn-main{width:100%;padding:13px 0;border:0;border-radius:12px;font-size:15px;font-weight:800;
  background:linear-gradient(135deg,var(--o),var(--o2));color:#fff;cursor:pointer}
.ga-btn-main:disabled{background:rgba(255,255,255,.10);color:rgba(255,255,255,.45);cursor:default}

.ga-cal{padding:16px}
.ga-cal-h{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px}
.ga-cal-streak{font-size:13px;opacity:.85}
.ga-cal-streak b{color:var(--o);font-size:16px}
.ga-cal-wd,.ga-cal-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:5px}
.ga-cal-wd{margin-bottom:5px}
.ga-cal-wd span{text-align:center;font-size:11px;opacity:.45}
.ga-cal-d{aspect-ratio:1;display:flex;align-items:center;justify-content:center;
  border-radius:9px;font-size:12px;background:rgba(255,255,255,.05);opacity:.55}
.ga-cal-d.on{background:linear-gradient(135deg,var(--o),var(--o2));color:#fff;opacity:1;font-weight:800}
.ga-cal-d.today{outline:1.5px solid var(--o);opacity:1}
.ga-cal .ga-btn-main{margin-top:14px}

.ga-box{padding:16px}
.ga-box-h{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:12px}
.ga-btn-box{width:100%;padding:14px 0;border:0;border-radius:12px;font-size:15px;font-weight:800;
  background:linear-gradient(135deg,#FFB020,#FF6B00);color:#fff;cursor:pointer}
.ga-box-lock{padding:12px;border-radius:10px;background:rgba(255,255,255,.05);
  font-size:13px;line-height:1.6;text-align:center}
.ga-box-wait{font-size:13px;opacity:.85}
.ga-box-wait b{color:var(--o);font-size:16px}
.ga-box-bar{height:6px;border-radius:3px;background:rgba(255,255,255,.08);margin-top:9px;overflow:hidden}
.ga-box-bar i{display:block;height:100%;background:linear-gradient(90deg,var(--o),var(--o2))}

.ga-sumgrid{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:rgba(255,255,255,.06)}
.ga-sumcell{display:flex;flex-direction:column;gap:3px;padding:12px;background:var(--bg1)}
.ga-sumcell b{font-size:15px}
.ga-plus{color:#35D07F}
.ga-minus{color:#FF6B6B}
.ga-histbox{max-height:320px;overflow:auto}
.ga-logrow{display:flex;justify-content:space-between;align-items:center;
  padding:10px 12px;border-bottom:1px solid rgba(255,255,255,.05)}
.ga-logrow:last-child{border-bottom:0}

.ga-boxpop{position:fixed;inset:0;background:rgba(0,0,0,.65);z-index:300;
  display:flex;align-items:center;justify-content:center}
.ga-boxpop-in{background:var(--bg1);border-radius:18px;padding:28px 34px;text-align:center;
  display:flex;flex-direction:column;gap:7px;align-items:center}
.ga-boxpop-ic{font-size:50px}
.ga-boxpop-in b{font-size:24px;color:var(--o)}

/* ───── 미션 */
.ga-streak-card{position:relative;background:linear-gradient(135deg,var(--o),#EA580C);
 border-radius:18px;padding:13px;overflow:hidden}
.ga-streak-ch{position:absolute;right:6px;top:-6px;width:86px;opacity:.95}
.ga-streak-h{font-size:17px;font-weight:900}
.ga-streak-h b{font-size:22px}
.ga-streak-b{display:flex;align-items:center;justify-content:space-between;gap:7px;margin-top:6px;
 position:relative;z-index:2}
.ga-streak-bonus{background:#FFF7E8;color:#7C2D12;border-radius:10px;padding:5px 9px;
 font-size:10px;font-weight:800}
.ga-streak-bonus b{display:block;font-size:13px}
.ga-streak-days{display:flex;gap:4px;margin-top:11px}
.ga-sd{flex:1;display:flex;flex-direction:column;align-items:center;gap:3px}
.ga-sd span{width:100%;aspect-ratio:1;max-width:32px;border-radius:50%;background:rgba(255,255,255,.28);
 display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:900}
.ga-sd.on span{background:#fff;color:var(--o)}
.ga-sd.gift span{background:var(--gold);color:#7C2D12}
.ga-sd small{font-size:9px;opacity:.9}

.ga-ms{display:flex;gap:9px;align-items:center;background:var(--cd);border:1px solid var(--bd);
 border-radius:16px;padding:10px}
.ga-ms-ic{width:50px;height:50px;border-radius:12px;object-fit:cover;flex:0 0 auto}
.ga-ms-mid{flex:1;min-width:0}
.ga-ms-t{font-size:13px;font-weight:800}
.ga-ms-d{font-size:10px;color:var(--dim);margin:2px 0 5px}
.ga-ms-bar{position:relative;height:7px;border-radius:999px;background:rgba(255,255,255,.12)}
.ga-ms-bar div{height:100%;border-radius:999px;background:linear-gradient(90deg,var(--o),var(--o2))}
.ga-ms-bar em{position:absolute;right:0;top:-15px;font-size:9px;color:var(--dim);font-style:normal}
.ga-ms-r{display:flex;flex-direction:column;gap:5px;align-items:flex-end;flex:0 0 auto}
.ga-ms-rw{background:rgba(255,197,61,.14);color:var(--gold);border-radius:8px;padding:4px 8px;
 font-size:10px;font-weight:800;white-space:nowrap}
.ga-pass{display:flex;gap:8px;align-items:center}
.ga-pass img{width:58px;flex:0 0 auto}

/* ───── 확신도 */
.ga-stake{background:var(--cd);border:1px solid var(--bd);border-radius:14px;padding:10px}
.ga-stake-l{font-size:11px;color:var(--dim);text-align:center;margin-bottom:8px}
.ga-stake-btns{display:grid;grid-template-columns:repeat(3,1fr);gap:7px}
.ga-stake-b{border-radius:12px;border:2px solid var(--bd);background:rgba(255,255,255,.05);
 color:#D6E0F5;padding:9px 4px;cursor:pointer;display:flex;flex-direction:column;gap:2px}
.ga-stake-b b{font-size:14px;font-weight:900}
.ga-stake-b small{font-size:9px;opacity:.75}
.ga-stake-b.s3.on{border-color:#F59E0B;background:rgba(245,158,11,.18);color:#fff}
.ga-stake-b.s10.on{border-color:#EF4444;background:rgba(239,68,68,.2);color:#fff;
 animation:gaPulse 1.4s ease-in-out infinite}
.ga-stake-b.s1.on{border-color:#22C55E;background:rgba(34,197,94,.16);color:#fff}

/* ───── 연승 도전 */
.ga-srun{background:linear-gradient(135deg,#2A1B08,#16264F);border:1px solid rgba(255,197,61,.3);
 border-radius:14px;padding:11px;display:flex;flex-direction:column;gap:6px;align-items:center;text-align:center}
.ga-srun.live{border-color:var(--o);box-shadow:0 0 22px rgba(255,107,0,.25)}
.ga-srun-h{font-size:14px;font-weight:800}
.ga-srun-h b{color:var(--o2);font-size:17px}
.ga-srun-m{color:var(--gold);font-weight:900;margin-left:5px}
.ga-srun-s{font-size:11px;color:#CBD5E1;line-height:1.45}
.ga-srun-s b{color:var(--gold)}

/* ───── 코인 / 상점 */
.ga-coinbar{display:flex;align-items:center;gap:8px;padding:8px 12px;font-size:12px;font-weight:800;
 background:rgba(255,197,61,.1);border-bottom:1px solid rgba(255,197,61,.25);
 color:var(--gold);cursor:pointer}
.ga-coinbar-s{color:var(--o2)}
.ga-coinbar-go{margin-left:auto;color:var(--dim);font-weight:600}
.ga-coinbox{display:flex;align-items:center;gap:11px;
 background:linear-gradient(135deg,#2A1B08,#16264F);border-color:rgba(255,197,61,.35)}
.ga-coinbox img{width:60px}
.ga-coinbox-v{font-size:26px;font-weight:900;color:var(--gold);line-height:1.1}
.ga-coinbox-n{text-align:right;margin-left:auto;line-height:1.4}
.ga-shop{display:flex;align-items:center;gap:10px;background:var(--cd);
 border:1px solid var(--bd);border-radius:14px;padding:10px}
.ga-shop img{width:46px;height:46px;object-fit:contain;flex:0 0 auto}
.ga-shop-mid{flex:1;min-width:0}
.ga-shop-t{font-size:14px;font-weight:800}
.ga-coinrow{display:grid;grid-template-columns:46px 1fr 62px;gap:7px;align-items:center;
 font-size:12px;padding:6px 0;border-bottom:1px solid rgba(120,160,255,.09)}
.ga-coinrow-n{color:#D6E0F5;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ga-coinrow b{text-align:right;font-variant-numeric:tabular-nums}

/* ───── 게임성: 실시간 상태 / 결과 연출 */
.ga-live-note{border-radius:14px;padding:11px 13px;text-align:center;
 border:2px solid;animation:gaPulse 1.6s ease-in-out infinite}
.ga-live-note.win{background:rgba(34,197,94,.14);border-color:#22C55E}
.ga-live-note.lose{background:rgba(239,68,68,.14);border-color:#EF4444}
.ga-live-t{font-size:17px;font-weight:900;display:flex;justify-content:center;gap:9px;align-items:baseline}
.ga-live-t b{font-variant-numeric:tabular-nums}
.ga-live-s{font-size:11px;color:#CBD5E1;margin-top:3px}
@keyframes gaPulse{0%,100%{filter:brightness(1)}50%{filter:brightness(1.25)}}

.ga-flash{position:fixed;left:50%;top:38%;transform:translate(-50%,-50%);z-index:60;
 background:rgba(8,14,32,.94);border:2px solid var(--o);border-radius:20px;
 padding:20px 30px;text-align:center;pointer-events:none;
 box-shadow:0 12px 40px rgba(0,0,0,.6);animation:gaPop .35s ease-out}
.ga-flash.cleared{border-color:var(--gold);box-shadow:0 0 50px rgba(255,197,61,.5)}
.ga-flash-big{font-size:38px;font-weight:900;color:var(--o2);line-height:1.1;
 text-shadow:0 3px 14px rgba(255,107,0,.6)}
.ga-flash.cleared .ga-flash-big{font-size:30px;color:var(--gold)}
.ga-flash-sub{font-size:12px;color:#CBD5E1;margin-top:6px}
@keyframes gaPop{0%{transform:translate(-50%,-50%) scale(.6);opacity:0}
 60%{transform:translate(-50%,-50%) scale(1.12)}100%{transform:translate(-50%,-50%) scale(1);opacity:1}}

.ga-hp-hint{font-size:11px;color:var(--dim);text-align:center;margin-top:6px}

/* 입장 안내 */
.ga-late{background:rgba(255,107,0,.12);border:1px solid rgba(255,107,0,.42);
 border-radius:14px;padding:11px 13px}
.ga-late-t{font-size:14px;font-weight:800;color:var(--o2)}
.ga-late-d{font-size:11px;color:#CBD5E1;margin-top:4px;line-height:1.55}
.ga-ai-note{font-size:11px;color:#93C5FD;background:rgba(59,130,246,.12);
 border:1px solid rgba(59,130,246,.3);border-radius:12px;padding:8px 11px;text-align:center}
.ga-ai-note b{color:#BFDBFE}

/* ───── 시즌 */
.ga-season-card{background:linear-gradient(135deg,#2A1B08,#16264F);border-color:rgba(255,197,61,.35)}
.ga-season-head{display:flex;justify-content:space-between;align-items:center;font-size:14px;font-weight:800}
.ga-season-row{display:flex;align-items:center;gap:8px;margin:9px 0 5px}
.ga-season-lg{font-size:17px;font-weight:900}
.ga-season-rw{display:flex;gap:6px;margin-top:9px}
.ga-season-rw>div{flex:1;background:rgba(0,0,0,.3);border-radius:10px;padding:7px 8px;text-align:center}
.ga-season-rw b{display:block;font-size:14px;font-weight:900;margin-top:2px}
.ga-gold-t{color:var(--gold)}
.ga-unclaimed{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:9px;
 background:rgba(255,107,0,.16);border:1px solid rgba(255,107,0,.45);border-radius:12px;
 padding:8px 10px;font-size:11px}
.ga-unclaimed b{color:var(--gold)}
.ga-lgtable{display:flex;flex-direction:column;gap:4px;margin-bottom:8px}
.ga-lgtable-r{display:grid;grid-template-columns:1fr auto 64px;gap:8px;align-items:center;
 font-size:12px;padding:6px 8px;border-radius:9px;background:rgba(255,255,255,.05)}
.ga-lgtable-r.on{background:rgba(255,197,61,.16);border:1px solid rgba(255,197,61,.45)}
.ga-lgtable-r span{font-weight:900}
.ga-lgtable-r em{font-style:normal;color:var(--dim);font-size:10px}
.ga-lgtable-r b{text-align:right;color:var(--gold)}

/* ───── HUMAN vs AI */
.ga-hva-hero{display:flex;align-items:center;justify-content:center;gap:4px;padding:6px 0}
.ga-hva-l,.ga-hva-r{width:88px}
.ga-hva-vs{width:74px}
.ga-hva-t{font-size:23px;font-weight:900;letter-spacing:.5px}
.ga-hva-t span{color:var(--o2);font-style:italic}
.ga-score{display:flex;align-items:center;gap:8px;background:var(--cd);border:1px solid var(--bd);
 border-radius:16px;padding:12px}
.ga-score-s{flex:1;text-align:center;background:rgba(255,107,0,.12);border:1px solid rgba(255,107,0,.35);
 border-radius:12px;padding:8px 4px}
.ga-score-ai{background:rgba(59,130,246,.12);border-color:rgba(59,130,246,.4)}
.ga-score-l{font-size:11px;font-weight:800}
.ga-score-v{font-size:28px;font-weight:900;line-height:1.1}
.ga-score-mid{text-align:center;min-width:56px}
.ga-score-mid b{font-size:14px}
.ga-ai{border-color:rgba(59,130,246,.35)}
.ga-ai-head{display:flex;justify-content:space-between;align-items:center;font-size:13px;font-weight:800}
.ga-ai-conf{font-size:11px;color:#93C5FD}
.ga-ai-bar{height:7px;border-radius:999px;background:rgba(255,255,255,.12);margin:7px 0;overflow:hidden}
.ga-ai-bar div{height:100%;background:linear-gradient(90deg,#2563EB,#60A5FA)}
.ga-ai-reason{font-size:11px;color:#CBD5E1;line-height:1.45}
.ga-ai-pick{margin-top:8px;padding:8px;border-radius:10px;background:rgba(0,0,0,.3);
 font-size:12px;text-align:center;font-weight:700}
.ga-ai-pick.hidden{color:var(--dim);font-weight:400}
.ga-q-vote{margin-top:9px;font-size:11px;color:#7A88A6;text-align:center}
.ga-q-vote b{color:#152040}
.ga-hist-th,.ga-hist-r{display:grid;grid-template-columns:28px 1fr 1fr 1fr;gap:4px;font-size:12px;
 text-align:center;align-items:center}
.ga-hist-th{color:var(--dim);font-size:10px;padding-bottom:6px;border-bottom:1px solid var(--bd)}
.ga-hist-r{padding:7px 0;border-bottom:1px solid rgba(120,160,255,.09)}
.ga-win{color:var(--up);font-weight:800}
.ga-lose{color:var(--dim)}

/* ───── 클랜 */
.ga-war{background:linear-gradient(135deg,#2A1230,#16264F);border-color:rgba(255,107,0,.3)}
.ga-war-head{display:flex;justify-content:space-between;align-items:center;font-size:14px;font-weight:800}
.ga-war-vs{display:flex;align-items:center;justify-content:space-around;gap:6px;padding:10px 0}
.ga-war-side{flex:1;text-align:center;border-radius:12px;padding:8px 4px;border:1px solid transparent}
.ga-war-side.mine{background:rgba(255,107,0,.14);border-color:rgba(255,107,0,.45)}
.ga-war-side img{width:48px;height:48px;object-fit:contain}
/* 방패 이미지는 하나뿐이라 상대편은 색만 돌려서 구분한다 */
.ga-em-b{filter:hue-rotate(185deg) saturate(1.35)}
.ga-em-dia{filter:hue-rotate(160deg) saturate(1.2) brightness(1.15)}
.ga-war-name{font-size:12px;font-weight:800;margin-top:3px}
.ga-war-score{font-size:22px;font-weight:900}
.ga-war-vsimg{width:46px;flex:0 0 auto}
.ga-war-bar{height:10px;border-radius:999px;background:#3B82F6;overflow:hidden;margin-bottom:7px}
.ga-war-bar div{height:100%;background:linear-gradient(90deg,var(--o),var(--o2));transition:width .6s}
.ga-contrib{margin-top:10px;border-top:1px solid var(--bd);padding-top:9px}
.ga-contrib-r{display:grid;grid-template-columns:22px 1fr auto;gap:7px;font-size:12px;padding:5px 0}
.ga-contrib-r span:first-child{color:var(--gold);font-weight:900;text-align:center}
.ga-contrib-r em{font-style:normal;color:var(--dim);font-size:11px}
.ga-myclan{display:flex;align-items:center;gap:10px}
.ga-myclan img{width:46px;height:46px;object-fit:contain;flex:0 0 auto}
.ga-myclan-mid{flex:1;min-width:0}
.ga-myclan-n{font-size:14px;font-weight:800}
.ga-form{display:flex;gap:6px;margin-bottom:7px}
.ga-in{flex:1;min-width:0;background:rgba(255,255,255,.08);border:1px solid var(--bd);border-radius:10px;
 padding:10px 11px;color:#fff;font-size:14px;outline:none}
.ga-in::placeholder{color:#6B7DA3}
.ga-in:focus{border-color:var(--o)}
.ga-in-s{flex:0 0 78px}
.ga-cl-th,.ga-cl-r{display:grid;grid-template-columns:26px 1fr 40px 58px 52px;gap:4px;align-items:center}
.ga-cl-th{font-size:10px;color:var(--dim);padding-bottom:7px;border-bottom:1px solid var(--bd)}
.ga-cl-th span:nth-child(3),.ga-cl-th span:nth-child(4){text-align:right}
.ga-cl-r{padding:8px 0;border-bottom:1px solid rgba(120,160,255,.09);font-size:12px}
.ga-cl-u{display:flex;align-items:center;gap:7px;min-width:0}
.ga-cl-u img{width:28px;height:28px;object-fit:contain;flex:0 0 auto}
.ga-cl-u span{display:flex;flex-direction:column;min-width:0}
.ga-cl-u b{font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ga-cl-u small{font-size:9px;color:var(--dim)}
.ga-cl-m{text-align:right;color:var(--dim)}

/* ───── 채팅 */
.ga-chat{height:170px;overflow-y:auto;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;
 display:flex;flex-direction:column;gap:6px;padding:4px 2px;scrollbar-width:thin;
 background:rgba(0,0,0,.18);border-radius:10px}
.ga-chat::-webkit-scrollbar{width:4px}
.ga-chat::-webkit-scrollbar-thumb{background:rgba(255,255,255,.22);border-radius:99px}
.ga-chat-r{display:flex;gap:6px;align-items:baseline;font-size:12px;line-height:1.4}
.ga-chat-t{color:var(--dim);font-size:10px;flex:0 0 auto;font-variant-numeric:tabular-nums}
.ga-chat-n{color:var(--o2);font-weight:800;flex:0 0 auto;max-width:82px;white-space:nowrap;
 overflow:hidden;text-overflow:ellipsis}
.ga-chat-m{color:#D6E0F5;word-break:break-word}
.ga-chat-in{display:flex;gap:6px;margin-top:8px}
.ga-quick-msg{display:flex;gap:5px;margin-top:6px;flex-wrap:wrap}
.ga-quick-msg button{background:rgba(255,255,255,.08);border:1px solid var(--bd);border-radius:999px;
 padding:5px 10px;font-size:11px;color:#D6E0F5;cursor:pointer}
`;
